import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { safeCtaUrl } from "@/lib/popup-steps";
import {
  getMobileFlexDirection,
  getMobileContentClasses,
} from "@/lib/mobile-image-utils";
import {
  formatWhatsAppNumber,
  validateWhatsAppNumber,
} from "@/lib/popup-validation";
import {
  PopupWelcomeContent,
  PopupSuccessContent,
  PopupFormData,
  PopupFormErrors,
  PreviewMode,
  PopupFieldKey,
  PassPreviewData,
  PopupDeckContent,
  PopupDeckCard,
  PopupIntroContent,
  PopupScratchContent,
  PopupQuestion,
  PopupWheelContent,
  PopupWheelSpinResult,
  PopupPhonePageContent,
} from "@/shared/types/popup-types";
import { PassShowcase } from "./passIntegration";
import { DeckStage, DeckAnswer } from "./DeckStage";
import { ScratchStage } from "./ScratchStage";
import { QuestionOptions } from "./QuestionOptions";
import { PhonePage } from "./PhonePage";
import { PlinkoStage } from "./PlinkoStage";
import { CardsStage } from "./CardsStage";
import { WheelStage, WheelStatus } from "./WheelStage";
import {
  WHEEL_DEFAULTS,
  autoApplyUrl,
  formatCountdown,
  normalizeSegments,
  pickSegment,
  resultHeading,
  fillPrizeToken,
} from "@/lib/wheel";
import { PASSES_MARK } from "./passesMark";
import {
  EMPTY_BIRTHDAY,
  birthdayFromParts,
  birthdayPartInput,
  type BirthdayParts,
} from "@/lib/birthday";
import type { IssuedPass } from "../../lib/popup-api";
import { EditOverlay } from "./EditOverlay";
import {
  NormalizedPopupDesign,
  ResolvedStepDesign,
  PopupFormat,
} from "@/shared/types/popup-design";
import {
  backgroundCss,
  backgroundImageValue,
  mobileBackdropLayers,
  mobileSizeProps,
  spaceAboveProps,
  fieldGapProps,
  overlayCss,
  resolveModalWidths,
  shadowCss,
  litButtonStyle,
  paddingScale,
  textCss,
  fontStack,
  ensureFontsLoaded,
  entryAnimationCss,
  RENDERER_CSS,
  FORM_MAX_WIDTH,
  sanitizeInlineHtml,
  isLightBackground,
  isLightColor,
} from "./engine";

// WhatsApp SVG icon (unchanged from the legacy renderer)
const WhatsAppIcon: React.FC<{ className?: string; style?: React.CSSProperties }> = ({
  className,
  style,
}) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z" />
  </svg>
);

type PopupStep = "intro" | "scratch" | "question" | "welcome" | "phone" | "wheel" | "deck" | "success";

/** Two-rectangle copy glyph, drawn inline so the popup never depends on an
 *  icon font or a second lucide import on the storefront bundle. Inherits
 *  currentColor so it works on light and dark panels alike. */
const CopyGlyph: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

export interface PopupRendererProps {
  currentStep: PopupStep;
  welcomeContent: PopupWelcomeContent;
  successContent: PopupSuccessContent;
  normalized: NormalizedPopupDesign;

  isVisible: boolean;
  onClose: () => void;
  onButtonClick?: () => void;
  onSubmit?: (data: PopupFormData) => void;
  showCloseButton?: boolean;
  isSubmitting?: boolean;
  isTransitioning?: boolean;
  previewMode?: PreviewMode;
  globalErrorMessage?: string;
  /**
   * Re-fire the submission that just failed. Only the stages with no submit
   * button of their own (deck, scratch, standalone wheel) surface it — the
   * gate already has its own button to press again.
   */
  onRetrySubmit?: () => void;
  /** Loyalty-pass integration: pass visual in the image slot + wallet CTA. */
  passPreview?: PassPreviewData;
  /** Needed for client-side serial derivation (matches the issuing edge fn). */
  popupId?: string;
  /** Discount code minted for THIS signup, when the backend returns one.
   *  Takes precedence over the static code configured on the success step. */
  issuedDiscountCode?: string | null;
  /** Minted-code fetch state, from the widget: 'pending' while it polls after a
   *  submit, 'done' when the poll finished (with or without a code). undefined =
   *  no poll ever ran (static-code popups, previews) — behaviour unchanged. */
  issuedCodeStatus?: "pending" | "done";
  /** The wallet pass the SERVER issued for this signup (never derived here). */
  issuedPass?: IssuedPass | null;
  issuedPassStatus?: "pending" | "done";
  /** Builder direct manipulation: mounts the drag-resize overlay. Only ever
   *  set through the flowIQ editor's preview bridge — stores never see it. */
  editMode?: boolean;
  /** Intro-page content — present only when the popup enables the intro. */
  introContent?: PopupIntroContent;
  /** Intro CTA clicked: the widget advances the flow to the welcome form. */
  onIntroAdvance?: () => void;
  /** Swipe-deck step content — present only when the popup enables the deck. */
  deckContent?: PopupDeckContent;
  /** Fires once when the last card is answered, with every card's verdict. */
  onDeckComplete?: (answers: Record<string, DeckAnswer>) => void;
  /** Optional per-swipe analytics hook. */
  onDeckSwipe?: (card: PopupDeckCard, dir: DeckAnswer) => void;
  /** Scratch-card step content — present only when the popup enables it. */
  scratchContent?: PopupScratchContent;
  /** Scratch CTA clicked (post-reveal): advance the flow to the form. */
  onScratchAdvance?: () => void;
  /** The question page being shown (step "question"). */
  questionContent?: PopupQuestion;
  /** An answer was tapped: (question key, stored value). */
  onQuestionAnswer?: (key: string, value: string) => void;
  /** Phone page after the form (content_settings.phone_page). */
  phoneContent?: PopupPhonePageContent;
  onPhoneSubmit?: (whatsappNumber: string) => void;
  onPhoneSkip?: () => void;
  /** Fires once when the cover crosses the reveal threshold (analytics). */
  onScratchReveal?: () => void;
  /** Spin-to-win content — present only when the popup enables the wheel. */
  wheelContent?: PopupWheelContent;
  /** The wheel's lifecycle, owned by the widget (it owns the network). */
  wheelStatus?: WheelStatus;
  /** Wedge the server picked; read while wheelStatus === "result". */
  wheelResultIndex?: number | null;
  /** The wheel step's submit. Inline placement passes the validated form;
   *  "before" placement passes nothing (the widget holds the lead). */
  onWheelSpin?: (data?: PopupFormData) => void;
  /** Wheel came to rest on the wedge → the widget advances to success. */
  onWheelLanded?: (index: number) => void;
  /** Wheel coasted to a stop after a failed submit → widget resets to idle. */
  onWheelSettled?: () => void;
  /** What THIS visitor won — the success step's prize block. */
  wheelResult?: PopupWheelSpinResult | null;
  /** Absolute ISO expiry of the urgency countdown; null/undefined = no timer. */
  wheelExpiresAt?: string | null;
  /** "Powered by Flowapt" link target. Set by the widget only when the org has
   *  feature_flags.popup_powered_by on; undefined = no line (the default). */
  poweredByUrl?: string;
}

export const PopupRenderer: React.FC<PopupRendererProps> = ({
  currentStep,
  welcomeContent,
  successContent,
  normalized,
  isVisible,
  onClose,
  onButtonClick,
  onSubmit,
  showCloseButton = true,
  isSubmitting = false,
  isTransitioning = false,
  previewMode,
  globalErrorMessage,
  onRetrySubmit,
  passPreview,
  popupId,
  issuedDiscountCode,
  issuedCodeStatus,
  issuedPass,
  issuedPassStatus,
  editMode,
  introContent,
  onIntroAdvance,
  deckContent,
  onDeckComplete,
  onDeckSwipe,
  scratchContent,
  onScratchAdvance,
  onScratchReveal,
  questionContent,
  onQuestionAnswer,
  phoneContent,
  onPhoneSubmit,
  onPhoneSkip,
  wheelContent,
  wheelStatus,
  poweredByUrl,
  wheelResultIndex,
  onWheelSpin,
  onWheelLanded,
  onWheelSettled,
  wheelResult,
  wheelExpiresAt,
}) => {
  const [formData, setFormData] = useState<PopupFormData>({
    name: "",
    email: "",
    whatsapp_number: "+27",
    extra_fields: {},
  });
  const [localErrors, setLocalErrors] = useState<PopupFormErrors>({});
  // The birthday row's three boxes; the ISO date lands in extra_fields.birthday.
  const [birthParts, setBirthParts] = useState<BirthdayParts>(EMPTY_BIRTHDAY);
  const panelRef = useRef<HTMLDivElement>(null);

  const isWelcome = currentStep === "welcome";
  const isIntro = currentStep === "intro";
  const isDeck = currentStep === "deck";
  const isScratch = currentStep === "scratch";
  const isQuestion = currentStep === "question";
  const isPhone = currentStep === "phone";
  const isWheel = currentStep === "wheel";
  // Inline wheel = the fields sit BESIDE the wheel and the submit is the
  // spin — so the wheel step is a gate exactly like the welcome form.
  const wheelInline =
    isWheel &&
    wheelContent?.form_placement !== "before" &&
    wheelContent?.form_placement !== "after";
  const isCardsGame = isWheel && wheelContent?.game === "cards";
  const isGate = isWelcome || wheelInline;
  // Overhang: the wheel floats half outside the panel edge (desktop only).
  const wheelOverhang = wheelInline && wheelContent?.layout === "overhang";
  // The intro wears the WELCOME step's design so intro → gate reads as one
  // continuous surface; the deck and scratch steps wear welcome + their own
  // steps.* overrides (the hook for the SVYP floating presentation).
  const step: ResolvedStepDesign = isDeck
    ? normalized.deck
    : isScratch
      ? normalized.scratch
      : isWheel
        ? normalized.wheel
        : isQuestion
          ? normalized.question
          : isPhone
            ? normalized.phone
          : isWelcome || isIntro
            ? normalized.welcome
            : normalized.success;
  const { design } = normalized;

  const isInteractionDisabled =
    previewMode === "intro" ||
    previewMode === "scratch" ||
    previewMode === "question" ||
    previewMode === "wheel" ||
    previewMode === "welcome" ||
    previewMode === "deck" ||
    previewMode === "success";
  // Hosted landing page: fully interactive INCLUDING close — the landing
  // page renders a branded backdrop (logo + gradient) that shows through
  // after dismissal, so closing never strands the visitor on a void.
  const isPageEmbed = previewMode === "page";

  // Phone viewport tracking for mobileFormat (a modal that goes edge-to-edge
  // on phones). Listened, not just read, so a rotation/resize re-lays out.
  const [isPhoneViewport, setIsPhoneViewport] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsPhoneViewport(mq.matches);
    onChange();
    if (typeof mq.addEventListener === "function") mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (typeof mq.removeEventListener === "function") mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  // "card" = a full-height card inset from the screen edges (Alia's Aether
  // mobile layout: the overlay colour frames a rounded card). It rides the
  // fullscreen path — same auto-margin vertical centering — with an inset.
  const phoneCard =
    design.format === "modal" && design.mobileFormat === "card" && isPhoneViewport;
  // Desktop twin: `cardFill` makes the modal an inset, full-height card too.
  const desktopCard = design.format === "modal" && !!design.cardFill && !isPhoneViewport;
  const cardMode = phoneCard || desktopCard;
  const format: PopupFormat =
    design.format === "modal" &&
    ((design.mobileFormat === "fullscreen" && isPhoneViewport) || cardMode)
      ? "fullscreen"
      : design.format;
  const cardInset = phoneCard
    ? String(design.mobileCardInset || "").trim() || "5vw"
    : String(design.cardInset || "").trim() || "16px";
  const isModalLike = format === "modal" || format === "fullscreen";
  const isBar = format === "bar";
  const isSlidein = format === "slidein";
  // Floating presentation (SVYP deck look): no panel chrome, elements sit
  // straight on the overlay. Blocking formats only — a slide-in/bar without
  // a panel would just be loose text on the merchant's page.
  const frameless = isModalLike && step.theme.frameless === true;
  // "screen" pins the ✕ to the viewport's top-right, outside the panel.
  const closeOnScreen =
    isModalLike && step.elements.closeButton.placement === "screen";

  // ---------- side effects ----------

  // Load every family the design uses — theme + per-element overrides, for
  // BOTH steps so the success step never flashes a fallback font. Families
  // matching an org-uploaded custom font load via @font-face instead of
  // Google Fonts.
  useEffect(() => {
    const families: Array<string | undefined> = [];
    for (const s of [normalized.welcome, normalized.success]) {
      families.push(
        s.theme.fontFamily,
        s.elements.heading.fontFamily,
        s.elements.subheading.fontFamily,
        s.elements.body.fontFamily,
        s.elements.footer.fontFamily
      );
    }
    ensureFontsLoaded(families, normalized.design.customFonts);
  }, [normalized]);

  // ---- spin-to-win state ----
  const wheelSegments = useMemo(() => normalizeSegments(wheelContent), [wheelContent]);
  // Static previews (editor / hosted preview modes) get a DEMO spin: a local
  // weighted pick that animates exactly like the real thing but submits
  // nothing — so a designer can watch the wheel without creating a lead.
  const [demoSpin, setDemoSpin] = useState<{ status: WheelStatus; index: number | null }>({
    status: "idle",
    index: null,
  });
  const demoTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (demoTimerRef.current) window.clearTimeout(demoTimerRef.current);
  }, []);
  const wheelDemo = isWheel && isInteractionDisabled;
  const effectiveWheelStatus: WheelStatus = wheelDemo
    ? demoSpin.status
    : (wheelStatus ?? "idle");
  const effectiveWheelIndex = wheelDemo ? demoSpin.index : (wheelResultIndex ?? null);
  const wheelBusy =
    isWheel && (effectiveWheelStatus === "spinning" || effectiveWheelStatus === "result");
  const startDemoSpin = useCallback(() => {
    if (wheelSegments.length === 0 || demoSpin.status !== "idle") return;
    setDemoSpin({ status: "spinning", index: null });
    demoTimerRef.current = window.setTimeout(() => {
      setDemoSpin({ status: "result", index: pickSegment(wheelSegments) });
    }, 900);
  }, [wheelSegments, demoSpin.status]);
  const onDemoLanded = useCallback(() => {
    demoTimerRef.current = window.setTimeout(() => {
      setDemoSpin({ status: "idle", index: null });
    }, 1800);
  }, []);
  // Auto-apply target for the success CTA, resolved once the code is known
  // (below); a ref so the click handler defined earlier can read it.
  const wheelApplyRef = useRef<string | null>(null);

  // Escape closes (not in static preview, never mid-spin — the submission
  // is in flight and the outcome is about to land)
  useEffect(() => {
    if (!isVisible || isInteractionDisabled || wheelBusy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isVisible, isInteractionDisabled, isPageEmbed, onClose, wheelBusy]);

  // Move focus into the dialog when a blocking format opens
  useEffect(() => {
    if (isVisible && isModalLike && !isInteractionDisabled) {
      panelRef.current?.focus({ preventScroll: true });
    }
  }, [isVisible, isModalLike, isInteractionDisabled]);

  // Desktop modal taller than the window: shrink it to fit, the way a
  // visitor zooming out would. The desktop panel has no height cap (phones
  // get max-h-full + an inner scroll instead), and the overlay centres it, so
  // a panel taller than the window overflowed top AND bottom: the ✕ sat above
  // the screen and, with overlayClose off, the popup could not be closed
  // (Phyto, 19 Oct 2026: a 735px panel on 650-730px laptop windows). `scale`
  // is its own property, so it composes with the entry animation's transform,
  // and it leaves the layout box alone (offsetHeight stays the natural
  // height, so there is no measure loop). Off in the builder, whose drag
  // handles work in unscaled pixels.
  const [fitScale, setFitScale] = useState(1);
  const fitToWindow = isVisible && format === "modal" && !isPhoneViewport && !editMode;
  useLayoutEffect(() => {
    setFitScale(1);
    const panel = panelRef.current;
    const frame = panel?.parentElement;
    if (!fitToWindow || !panel || !frame || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const cs = window.getComputedStyle(frame);
      const room =
        frame.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
      const natural = panel.offsetHeight;
      if (!(room > 0) || !(natural > 0)) return;
      const next = natural > room ? Math.floor((room / natural) * 1000) / 1000 : 1;
      setFitScale((prev) => (Math.abs(prev - next) < 0.002 ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(panel);
    ro.observe(frame);
    return () => ro.disconnect();
  }, [fitToWindow, currentStep]);

  // ---------- form logic (ported from the legacy renderer) ----------

  const handleInputChange = useCallback(
    (field: keyof PopupFormData, value: string) => {
      let filteredValue = value;
      if (field === "whatsapp_number") {
        filteredValue = value.replace(/[^0-9+]/g, "");
      }
      setFormData((prev) => ({ ...prev, [field]: filteredValue }));
      setLocalErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
    },
    []
  );

  const handleChoiceChange = useCallback((key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      extra_fields: { ...(prev.extra_fields || {}), [key]: value },
    }));
    setLocalErrors((prev) =>
      prev.choices?.[key]
        ? { ...prev, choices: { ...prev.choices, [key]: "" } }
        : prev
    );
  }, []);

  const handleBirthdayPart = useCallback(
    (part: keyof BirthdayParts, raw: string) => {
      const next = { ...birthParts, [part]: birthdayPartInput(part, raw) };
      setBirthParts(next);
      const read = birthdayFromParts(next);
      setFormData((fd) => {
        const extras = { ...(fd.extra_fields || {}) };
        if (!read.empty && read.iso) extras.birthday = read.iso;
        else delete extras.birthday;
        return { ...fd, extra_fields: extras };
      });
      setLocalErrors((prev) => (prev.birthday ? { ...prev, birthday: "" } : prev));
    },
    [birthParts]
  );

  const validateForm = useCallback((): boolean => {
    if (!isGate || !welcomeContent) return true;
    const newErrors: PopupFormErrors = {};

    if (
      welcomeContent.name_field.enabled &&
      welcomeContent.name_field.required &&
      !formData.name.trim()
    ) {
      newErrors.name = "Name is required";
    }

    if (
      welcomeContent.email_field.enabled &&
      welcomeContent.email_field.required &&
      !formData.email.trim()
    ) {
      newErrors.email = "Email is required";
    } else if (
      welcomeContent.email_field.enabled &&
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    if (welcomeContent.whatsapp_field.enabled) {
      const whatsappError = validateWhatsAppNumber(formData.whatsapp_number || "");
      if (whatsappError) newErrors.whatsapp_number = whatsappError;
    }

    if (welcomeContent.birthday_field?.enabled) {
      const read = birthdayFromParts(birthParts);
      if (read.empty) {
        if (welcomeContent.birthday_field.required) newErrors.birthday = "Please enter your birthday";
      } else if (!read.iso) {
        newErrors.birthday = "Please enter a valid date";
      }
    }

    for (const cf of welcomeContent.choice_fields || []) {
      if (cf.enabled && cf.required && !formData.extra_fields?.[cf.key]) {
        newErrors.choices = {
          ...(newErrors.choices || {}),
          [cf.key]: "Please select an option",
        };
      }
    }

    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [isGate, welcomeContent, formData, birthParts]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (isInteractionDisabled) {
        // Editor / hosted previews: the spin button drives the DEMO wheel.
        if (isWheel) startDemoSpin();
        return;
      }
      if (!isGate || !validateForm()) return;
      const data: PopupFormData = {
        ...formData,
        // A switched-off WhatsApp field must not submit its "+27" prefill.
        whatsapp_number: !welcomeContent?.whatsapp_field?.enabled
          ? ""
          : formData.whatsapp_number
            ? formatWhatsAppNumber(formData.whatsapp_number)
            : formData.whatsapp_number,
      };
      // Inline wheel: the submit IS the spin — the widget persists the lead,
      // learns the wedge, and hands it back through wheelStatus/ResultIndex.
      if (isWheel) {
        if (!wheelBusy) onWheelSpin?.(data);
        return;
      }
      if (onSubmit) onSubmit(data);
    },
    [isGate, isWheel, wheelBusy, validateForm, onSubmit, onWheelSpin, formData, isInteractionDisabled, startDemoSpin, welcomeContent?.whatsapp_field?.enabled]
  );

  const handleButtonClick = () => {
    if (isInteractionDisabled) return;
    // Spin-to-win auto-apply: the success CTA puts the code on the cart
    // (Shopify /discount/… link, Woo ?coupon-code=…) before closing.
    // Otherwise a thank-you button with its own link (`success.button_url`)
    // opens it, the same way an intro CTA link does.
    const applyUrl = wheelApplyRef.current;
    const navUrl =
      applyUrl || (currentStep === "success" ? safeCtaUrl(successContent?.button_url) : null);
    if (navUrl && typeof window !== "undefined") {
      try {
        window.location.assign(navUrl);
      } catch {
        /* navigation blocked — fall through to the plain close */
      }
    }
    if (onButtonClick) onButtonClick();
    else onClose();
  };

  if (!isVisible) return null;

  // ---------- resolved styles ----------

  const widths = resolveModalWidths(step.size);
  const pads = paddingScale(step.theme.padding);

  const isImageBackground =
    step.image.enabled && step.image.position === "background" && !!step.image.url;
  // Layout-participating image. A store only shows a real image; the
  // builder's static preview also shows the placeholder block for an enabled
  // image with no URL yet, so uploads can be staged.
  const layoutImage =
    step.image.enabled &&
    step.image.position !== "background" &&
    (step.image.url || isInteractionDisabled)
      ? step.image
      : null;
  // Slide-ins are too narrow for side-by-side: side images render on top.
  // layoutImage excludes "background" placement, so this never carries it.
  const layoutImagePosition = (
    layoutImage && isSlidein && (layoutImage.position === "left" || layoutImage.position === "right")
      ? "top"
      : layoutImage?.position
  ) as Exclude<import("@/shared/types/popup-design").ImagePlacement, "background"> | undefined;

  // Loyalty pass takes over the visual slot: the layout behaves exactly as if
  // an image were configured there (keeping the designed image's position when
  // one exists), but the slot renders the wallet pass instead. Bars are too
  // small for a pass.
  const passActive = !!passPreview?.show_in_popup && !isBar;
  const hasVisual = passActive || !!layoutImage;
  const visualPosition: Exclude<
    import("@/shared/types/popup-design").ImagePlacement,
    "background"
  > | undefined = passActive
    ? layoutImagePosition ?? (isSlidein ? "top" : "left")
    : layoutImagePosition;

  const isSideImage = hasVisual && (visualPosition === "left" || visualPosition === "right");
  // Desktop only: on phones the side image is stacked (or hidden), and the
  // content must keep the auto-margin centering — a full-height row would
  // pin it to the top. The full-screen format gets the same full-height row
  // (8 Oct 2026): before, a side image in a takeover kept its natural height,
  // so a portrait photo made the page taller than the screen and scrolled with
  // the photo cut off, while a landscape one left bands of panel colour above
  // and below it. Now the photo column runs top to bottom like a card fill.
  const cardFillRow = (desktopCard || (format === "fullscreen" && !isPhoneViewport)) && isSideImage;
  const isVerticalImage = hasVisual && (visualPosition === "top" || visualPosition === "bottom");
  // Phone takeover (mobileFormat "fullscreen", not the inset card) whose
  // visual stacks above the form: getMobileFlexDirection puts every
  // position except "bottom" on top. A hidden-on-phone image has no visual.
  const phonePhotoOnTop =
    format === "fullscreen" &&
    isPhoneViewport &&
    !cardMode &&
    hasVisual &&
    visualPosition !== "bottom" &&
    (passActive || !layoutImage?.hideOnMobile);
  // Phone height cap for the stacked image (so the form never starts below
  // the fold). A takeover's own phone image (mobileUrl) is a crop made for
  // that screen, so it gets more room before object-cover trims it.
  const phoneImageCap =
    phonePhotoOnTop && !passActive && layoutImage?.mobileUrl ? "max-h-[50vh]" : "max-h-[38vh]";

  // Adaptive surfaces: light panels keep the EXACT legacy grays/reds (every
  // pre-2026 popup is white, so legacy rendering is unchanged); dark, colored
  // or image panels get treatments that stay visible on them.
  const panelIsLight = isImageBackground
    ? false
    : isLightBackground(step.theme.background);
  // Error border must contrast with the INPUT it wraps; error text with the
  // panel behind it. Light/light resolves to legacy red-500 exactly.
  const errorBorderColor = isLightColor(step.elements.inputs.background)
    ? "#ef4444"
    : "#f87171";
  const errorTextColor = panelIsLight ? "#ef4444" : "#fecaca";
  // Pill-shaped inputs need breathing room before the curve — and taller
  // pills have a deeper curve, so the padding scales with the input size.
  const inputRadiusPx = parseFloat(step.elements.inputs.radius);
  const pillInputs = !Number.isNaN(inputRadiusPx) && inputRadiusPx >= 20;
  const PILL_PAD: Record<string, string> = {
    sm: "18px",
    md: "20px", // legacy pill padding
    lg: "26px",
    xl: "30px",
  };
  const pillPad = PILL_PAD[step.elements.inputs.size ?? "md"] ?? "20px";
  const tallPills =
    pillInputs &&
    (step.elements.inputs.size === "lg" || step.elements.inputs.size === "xl");

  const panelBackground: React.CSSProperties = isImageBackground
    ? backgroundCss({
        type: "image",
        color: step.theme.background.color,
        image: {
          url: step.image.url,
          overlayColor: step.theme.background.image?.overlayColor ?? "#000000",
          overlayOpacity: step.theme.background.image?.overlayOpacity ?? 0.35,
        },
      })
    : backgroundCss(step.theme.background);
  // Phone-specific backdrop (<768px): a portrait base and/or transparent
  // edge strips pinned to the top and bottom of the visible screen. A single
  // landscape picture cover-cropped to a phone blows its edges up and loses
  // its bottom row behind the browser toolbar; edge strips never do.
  const mobileBackdrop = frameless
    ? null
    : isImageBackground
      ? mobileBackdropLayers({
          url: step.image.url,
          mobileUrl: step.image.mobileUrl,
          mobileTopUrl: step.theme.background.image?.mobileTopUrl,
          mobileBottomUrl: step.theme.background.image?.mobileBottomUrl,
          overlayColor: step.theme.background.image?.overlayColor ?? "#000000",
          overlayOpacity: step.theme.background.image?.overlayOpacity ?? 0.35,
        })
      : step.theme.background.type === "image" && step.theme.background.image
        ? mobileBackdropLayers(step.theme.background.image)
        : null;

  const panelStyles: React.CSSProperties = {
    ...(frameless ? { background: "transparent" } : panelBackground),
    borderRadius:
      (format === "fullscreen" && !cardMode) || isBar ? "0px" : step.theme.borderRadius,
    boxShadow: frameless ? "none" : shadowCss(step.theme.shadow),
    fontFamily: fontStack(step.theme.fontFamily),
    display: "flex",
    flexDirection: "column",
    // CSS vars consumed by RENDERER_CSS pseudo-class rules
    ["--fp-focus" as string]: step.elements.inputs.focusBorderColor,
    // Any Tailwind ring that survives anywhere in the panel paints in the
    // DESIGN's focus colour, never shadcn's default teal.
    ["--tw-ring-color" as string]: step.elements.inputs.focusBorderColor,
    ["--fp-placeholder" as string]: step.elements.inputs.placeholderColor,
    ["--fp-btn-hover" as string]:
      step.elements.button.hoverBackground ?? step.elements.button.background,
    ["--fp-radius-input" as string]: step.elements.inputs.radius,
    ["--fp-radius-btn" as string]: step.elements.button.radius,
    // 12px = the legacy px-3; pills need room before the curve. Rides a var +
    // !important rule because the shell's rem→px block (.px-3 { … !important })
    // beats INLINE padding — an inline paddingLeft here never paints.
    ["--fp-input-px" as string]: pillInputs ? pillPad : "12px",
    ...(mobileBackdrop
      ? {
          ["--fp-bg-m" as string]: mobileBackdrop.image,
          ["--fp-bg-m-size" as string]: mobileBackdrop.size,
          ["--fp-bg-m-pos" as string]: mobileBackdrop.position,
          ["--fp-bg-m-rep" as string]: mobileBackdrop.repeat,
        }
      : {}),
    ...(format === "modal"
      ? { width: widths.width, minWidth: widths.minWidth, maxWidth: widths.maxWidth }
      : {}),
    ...(format === "fullscreen" ? { width: "100%", height: "100%" } : {}),
    // the card's inset is the wrapper's padding — no extra mx-4 ring inside it
    ...(cardMode ? { margin: 0 } : {}),
    ...(isSlidein ? { width: "min(92vw, 400px)" } : {}),
    ...(isBar ? { width: "100%" } : {}),
    // Bars: anything that isn't explicitly "bottom" pins to the top — matches
    // the builder's Edge picker, incl. stale corner positions from a format
    // switch (e.g. slidein's bottom_right).
    ...entryAnimationCss(
      isBar
        ? design.position === "bottom"
          ? "slide_up"
          : "slide_down"
        : step.theme.animation.entry,
      step.theme.animation.durationMs
    ),
  };

  const headingStyles = textCss("heading", step.elements.heading);
  const subHeadingStyles = textCss("subheading", step.elements.subheading);
  // Exact phone sizes (mobileSizePx) ride a class + var so they can exceed
  // the desktop clamp under 768px.
  const headingMobile = mobileSizeProps(step.elements.heading);
  const subheadingMobile = mobileSizeProps(step.elements.subheading);
  // Designer-set space above each block (SpaceAbove); {} when unset, so a
  // popup that never sets one keeps the layout's own gaps.
  const headingSpace = spaceAboveProps(step.elements.heading);
  const subheadingSpace = spaceAboveProps(step.elements.subheading);
  const bodySpace = spaceAboveProps(step.elements.body);
  const footerSpace = spaceAboveProps(step.elements.footer);
  const inputsSpace = spaceAboveProps(step.elements.inputs);
  const fieldGap = fieldGapProps(step.elements.inputs);
  const buttonSpace = spaceAboveProps(step.elements.button);
  const messageStyles = textCss("message", step.elements.body);
  const footerStyles = textCss("footer", step.elements.footer);
  // Phone-only text-align overrides (fp-txt-m-* !important beats the inline
  // desktop textAlign under 768px); "" = inherit desktop.
  const headingAlignCls =
    step.elements.heading.mobileAlign && `fp-txt-m-${step.elements.heading.mobileAlign}`;
  const subheadingAlignCls =
    step.elements.subheading.mobileAlign &&
    `fp-txt-m-${step.elements.subheading.mobileAlign}`;
  const footerAlignCls =
    step.elements.footer.mobileAlign && `fp-txt-m-${step.elements.footer.mobileAlign}`;
  const bodyAlignCls =
    step.elements.body.mobileAlign && `fp-txt-m-${step.elements.body.mobileAlign}`;
  // Discount code box (thank-you page): its label and the code text follow
  // the message's alignment and size unless elements.code sets its own
  // (8 Oct 2026). Before this the code always sat at the left of its box
  // with the copy control on the right, whatever the copy around it did.
  const codeStyle = step.elements.code;
  const codeAlign = codeStyle.align || step.elements.body.align || "center";
  const codeMobileAlign = codeStyle.mobileAlign || step.elements.body.mobileAlign || "";
  const codeAlignCls = codeMobileAlign && `fp-txt-m-${codeMobileAlign}`;
  const codeLabelStyles = textCss("message", {
    ...step.elements.body,
    align: codeAlign,
    ...(codeStyle.sizePx > 0 ? { sizePx: codeStyle.sizePx } : {}),
  });
  const codeLabelMobile = mobileSizeProps(codeStyle.mobileSizePx > 0 ? codeStyle : step.elements.body);
  // "lit" finish: gradient + four shadows from the button colour (null = the
  // colour cannot be parsed, so it stays flat).
  const litButton =
    step.elements.button.finish === "lit"
      ? litButtonStyle(step.elements.button.background, step.elements.button.hoverBackground)
      : null;
  const buttonStyles: React.CSSProperties = {
    backgroundColor: step.elements.button.background,
    color: step.elements.button.textColor,
    borderRadius: step.elements.button.radius,
    boxShadow: shadowCss(step.elements.button.shadow),
    ...(step.elements.button.letterSpacing
      ? { letterSpacing: step.elements.button.letterSpacing }
      : {}),
    ...(litButton ?? {}),
  };
  const inputStyles = (hasError: boolean): React.CSSProperties => ({
    backgroundColor: step.elements.inputs.background,
    color: step.elements.inputs.textColor,
    borderColor: hasError ? errorBorderColor : step.elements.inputs.borderColor,
    borderRadius: step.elements.inputs.radius,
    // Inline width wins over the base border-2 class (legacy default is 2).
    borderWidth: `${step.elements.inputs.borderWidth ?? 2}px`,
  });
  const fieldErrorStyle: React.CSSProperties = { color: errorTextColor };

  // ---------- shared pieces ----------

  // The ✕ renders on EVERY surface so the design is honest about it:
  // functional on stores AND the hosted landing page; decorative-only in the
  // editor's static preview (click guarded), where hiding it used to mean
  // you could style the close button without ever seeing it.
  // design.hideCloseButton removes it — but ONLY while another close path
  // exists (dismiss element or overlay click), so a popup can never become
  // unclosable; Escape keeps working regardless.
  const xHidden =
    design.hideCloseButton && (design.dismiss?.enabled || design.overlayClose);
  const closeButton = showCloseButton && !xHidden && !closeOnScreen && (
    <button
      onClick={() => {
        if (!isInteractionDisabled) onClose();
      }}
      className={cn(
        "absolute rounded-full backdrop-blur-sm opacity-70 transition-all hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 z-10 shadow-lg",
        isBar
          ? "right-2 top-2 md:top-1/2 md:-translate-y-1/2 p-1.5"
          : "right-3 top-3 md:right-2 md:top-2 p-2"
      )}
      style={{
        color: step.elements.closeButton.color,
        backgroundColor: step.elements.closeButton.background,
      }}
      disabled={isSubmitting}
      aria-label="Close popup"
    >
      <X className={isBar ? "h-4 w-4" : "h-5 w-5"} />
      <span className="sr-only">Close</span>
    </button>
  );

  // Screen-placed ✕ (SVYP pattern): pinned to the overlay's top-right, a
  // touch larger than the panel variant since it floats free of any chrome.
  const screenCloseButton = showCloseButton && !xHidden && closeOnScreen && (
    <button
      onClick={() => {
        if (!isInteractionDisabled) onClose();
      }}
      className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-sm transition-opacity hover:opacity-80 focus:outline-none"
      style={{
        color: step.elements.closeButton.color,
        backgroundColor: step.elements.closeButton.background,
        border: "1px solid rgba(255,255,255,0.4)",
      }}
      disabled={isSubmitting}
      aria-label="Close popup"
    >
      <X className="h-5 w-5" />
      <span className="sr-only">Close</span>
    </button>
  );

  // "card" renders the image INSET inside the panel with its own corner
  // radius (the framed-photo look) instead of bleeding to the popup edges.
  // Placeholder (no URL) and bar-format images always stay flush.
  const isCardImage = !!layoutImage?.url && layoutImage.style === "card";

  // Optional phone-specific image: <picture> swaps the source below the md
  // breakpoint (the width at which the layout stacks). display:contents keeps
  // the img's own layout classes authoritative.
  const withMobileSource = (
    img: React.ReactElement,
    mobileUrl?: string
  ): React.ReactElement =>
    mobileUrl ? (
      <picture className="contents">
        <source media="(max-width: 767px)" srcSet={mobileUrl} />
        {img}
      </picture>
    ) : (
      img
    );
  const responsiveImg = (img: React.ReactElement): React.ReactElement =>
    withMobileSource(img, layoutImage?.mobileUrl);

  const imageSection =
    layoutImage &&
    (isCardImage ? (
      <div
        data-fp-edit="image"
        data-fp-edit-pos={visualPosition}
        className={cn(
          "relative flex-shrink-0 w-full p-[var(--fp-card-inset)]",
          layoutImage.hideOnMobile && "fp-hide-mobile",
          // builder drag-resize (fp-rz-*): explicit widths via vars +
          // !important rules; 0 = legacy auto sizing
          layoutImage.customWidthPct > 0 && "fp-rz-d",
          layoutImage.customWidthPct > 0 && isVerticalImage && "fp-rz-center",
          layoutImage.customMobileWidthPct > 0 && "fp-rz-m",
          isSideImage && "md:w-auto md:max-w-[55%] md:self-stretch md:flex-none",
          // When the card is STACKED against the content (mobile always;
          // top/bottom positions on desktop too), the content column already
          // provides the gap to the text — drop the card's gutter on the
          // text-facing edge so the spacing isn't doubled.
          visualPosition === "bottom"
            ? "pt-0"
            : isSideImage
              ? "pb-0 md:pb-[var(--fp-card-inset)]"
              : "pb-0"
        )}
        style={{
          ["--fp-card-inset" as string]: layoutImage.inset,
          ...(layoutImage.customWidthPct > 0
            ? { ["--fp-rz-d" as string]: `${layoutImage.customWidthPct}%` }
            : {}),
          ...(layoutImage.customMobileWidthPct > 0
            ? { ["--fp-rz-m" as string]: `${layoutImage.customMobileWidthPct}%` }
            : {}),
        }}
      >
        <div
          className={cn(
            "overflow-hidden w-full max-w-full",
            isSideImage && "md:h-full md:w-fit"
          )}
          style={{ borderRadius: layoutImage.radius }}
        >
          {responsiveImg(
            <img
              src={layoutImage.url}
              alt={layoutImage.alt || (isWelcome ? "Popup image" : "Success image")}
              className={cn(
                // mobile: cap the image so the form never starts below the fold
                "block w-full h-auto",
                phoneImageCap,
                layoutImage.fit === "contain" ? "object-contain" : "object-cover",
                isVerticalImage && "md:max-h-[300px]",
                isSideImage && "md:w-auto md:h-full md:max-w-full md:max-h-none"
              )}
            />
          )}
        </div>
      </div>
    ) : (
      <div
        data-fp-edit="image"
        data-fp-edit-pos={visualPosition}
        className={cn(
          "relative overflow-hidden flex-shrink-0 w-full",
          layoutImage.hideOnMobile && "fp-hide-mobile",
          // builder drag-resize (fp-rz-*): explicit widths via vars +
          // !important rules; 0 = legacy auto sizing
          layoutImage.customWidthPct > 0 && "fp-rz-d",
          layoutImage.customWidthPct > 0 && isVerticalImage && "fp-rz-center",
          layoutImage.customMobileWidthPct > 0 && "fp-rz-m",
          // cover: classic stretch-to-column-height split, capped at 55%.
          // contain promises the WHOLE image, uncropped, at its NATURAL shape —
          // so it must never be h-full-stretched (a mismatched box crops or
          // letterboxes, slicing/faking margins on assets with baked-in
          // frames). Instead the column takes widthPct of the row and the
          // image's height follows its own aspect (w-full h-auto), vertically
          // centered when the content column is taller. Fixed width share =
          // no size feedback loop, no overflow at narrow viewports.
          isSideImage &&
            (layoutImage.fit === "contain"
              ? "md:flex-none md:self-stretch md:w-[var(--fp-img-w)] md:flex md:items-center"
              : "md:w-auto md:max-w-[55%] md:self-stretch md:flex-none"),
          isVerticalImage && "md:max-h-[300px]",
          !layoutImage.url &&
            "bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center"
        )}
        style={{
          ...(isSideImage && layoutImage.fit === "contain" && layoutImage.url
            ? { ["--fp-img-w" as string]: `${layoutImage.widthPct || 45}%` }
            : {}),
          ...(layoutImage.customWidthPct > 0
            ? { ["--fp-rz-d" as string]: `${layoutImage.customWidthPct}%` }
            : {}),
          ...(layoutImage.customMobileWidthPct > 0
            ? { ["--fp-rz-m" as string]: `${layoutImage.customMobileWidthPct}%` }
            : {}),
        }}
      >
        {layoutImage.url ? (
          responsiveImg(
            <img
              src={layoutImage.url}
              alt={layoutImage.alt || (isWelcome ? "Popup image" : "Success image")}
              className={cn(
                // mobile: cap the image so the form never starts below the fold
                "block w-full h-auto",
                phoneImageCap,
                layoutImage.fit === "contain" ? "object-contain" : "object-cover",
                isVerticalImage && "md:max-h-[300px]",
                isSideImage &&
                  (layoutImage.fit === "contain"
                    ? "md:max-h-none"
                    : "md:w-auto md:h-full md:max-w-full md:max-h-none")
              )}
            />
          )
        ) : (
          <div className="text-center text-white p-4 md:p-8">
            <div className="w-20 h-20 md:w-32 md:h-32 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
              {isWelcome ? (
                <WhatsAppIcon className="w-10 h-10 md:w-16 md:h-16 text-white" />
              ) : (
                <Check className="w-10 h-10 md:w-16 md:h-16 text-white" />
              )}
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-2">
              {isWelcome ? "Welcome!" : "Success!"}
            </h3>
            <p className="text-white/90 text-base md:text-lg">
              {isWelcome ? "Join our community today" : "Thank you for joining us"}
            </p>
          </div>
        )}
      </div>
    ));

  // Loyalty pass in the visual slot — live-bound to the form (typing your name
  // updates the pass) and rendered by the admin dashboard's exact engine.
  // The pass IS the visual slot (see the passActive note above), so it carries
  // the image slot's builder affordances too: data-fp-edit makes it selectable
  // and drag-resizable, writing the SAME image.customWidthPct the designed
  // image uses. Read from step.image rather than layoutImage so the drag still
  // works when the pass has taken over a slot with no image configured behind
  // it (layoutImage is null in that case).
  const passWidthPct = step.image.customWidthPct ?? 0;
  const passMobileWidthPct = step.image.customMobileWidthPct ?? 0;
  const passSection = passActive && passPreview && (
    <div
      data-fp-edit="image"
      data-fp-edit-pos={visualPosition}
      className={cn(
        "relative overflow-hidden flex-shrink-0 w-full",
        // builder drag-resize (fp-rz-*): explicit widths via vars +
        // !important rules; 0 = legacy auto sizing
        passWidthPct > 0 && "fp-rz-d",
        passWidthPct > 0 && isVerticalImage && "fp-rz-center",
        passMobileWidthPct > 0 && "fp-rz-m",
        // 300px is the DEFAULT comfortable width for a 240px card + padding,
        // not a hard law. Drop it once the builder has set an explicit width:
        // fp-rz-* forces width/max-width !important but never min-width, so a
        // surviving floor would silently veto any narrower drag.
        isSideImage && passWidthPct === 0 && "md:min-w-[300px]",
        isSideImage && "md:w-auto md:max-w-[45%] md:self-stretch md:flex-none",
        isVerticalImage && "md:max-h-[340px]"
      )}
      style={{
        ...(passWidthPct > 0
          ? { ["--fp-rz-d" as string]: `${passWidthPct}%` }
          : {}),
        ...(passMobileWidthPct > 0
          ? { ["--fp-rz-m" as string]: `${passMobileWidthPct}%` }
          : {}),
      }}
    >
      <PassShowcase passPreview={passPreview} form={formData} issuedPass={issuedPass} />
    </div>
  );

  const visualSection = passActive ? passSection : imageSection;

  // Bars condense the fields into an inline row; everything else stacks.
  // Input height scale — "md" is the legacy height, kept byte-identical.
  const INPUT_HEIGHT_CLS: Record<string, string> = {
    sm: "h-10 text-sm",
    md: "h-11 md:h-12 text-sm md:text-base",
    lg: "h-12 md:h-14 text-base",
    xl: "h-14 md:h-16 text-base md:text-lg",
  };
  const fieldHeightCls = isBar
    ? "h-10 text-sm"
    : INPUT_HEIGHT_CLS[step.elements.inputs.size ?? "md"] ?? INPUT_HEIGHT_CLS.md;
  const fieldErrorCls = isBar ? "text-xs mt-1" : "text-sm mt-2";

  // Builder drag-resize on the form controls (fp-fld-*): the model holds ONE
  // InputsStyle, so all fields resize together; the button has its own set.
  // 0 = auto (legacy sizing). Width rides the wrapper, height vars cascade to
  // the inputs inside (div.fp-fld-h-* input rule in RENDERER_CSS).
  const inpStyle = step.elements.inputs;
  const btnStyle = step.elements.button;
  // Fields hold still (locked, undimmed) from the moment a spin fires until
  // the wheel lands — the lead is already in flight.
  const fieldsLocked = isSubmitting || isInteractionDisabled || wheelBusy;
  const fieldResizeCls = cn(
    inpStyle.customWidthPct > 0 && "fp-fld-w-d",
    inpStyle.customMobileWidthPct > 0 && "fp-fld-w-m",
    inpStyle.customHeightPx > 0 && "fp-fld-h-d",
    inpStyle.customMobileHeightPx > 0 && "fp-fld-h-m",
    // alignment is per-device: mobileAlign falls back to align
    (inpStyle.customWidthPct > 0 || inpStyle.customMobileWidthPct > 0) &&
      `fp-fld-d-${inpStyle.align || "center"}`,
    (inpStyle.customWidthPct > 0 || inpStyle.customMobileWidthPct > 0) &&
      `fp-fld-m-${inpStyle.mobileAlign || inpStyle.align || "center"}`
  );
  const fieldResizeStyle: React.CSSProperties = {
    ...(inpStyle.customWidthPct > 0
      ? { ["--fp-fld-w-d" as string]: `${inpStyle.customWidthPct}%` }
      : {}),
    ...(inpStyle.customMobileWidthPct > 0
      ? { ["--fp-fld-w-m" as string]: `${inpStyle.customMobileWidthPct}%` }
      : {}),
    ...(inpStyle.customHeightPx > 0
      ? { ["--fp-fld-h-d" as string]: `${inpStyle.customHeightPx}px` }
      : {}),
    ...(inpStyle.customMobileHeightPx > 0
      ? { ["--fp-fld-h-m" as string]: `${inpStyle.customMobileHeightPx}px` }
      : {}),
  };
  const buttonResizeCls = cn(
    btnStyle.customWidthPct > 0 && "fp-fld-w-d",
    btnStyle.customMobileWidthPct > 0 && "fp-fld-w-m",
    btnStyle.customHeightPx > 0 && "fp-fld-h-d",
    btnStyle.customMobileHeightPx > 0 && "fp-fld-h-m",
    btnStyle.fontSizePx > 0 && "fp-btn-fs-d",
    btnStyle.mobileFontSizePx > 0 && "fp-btn-fs-m",
    // alignment is per-device: mobileAlign falls back to align
    (btnStyle.customWidthPct > 0 || btnStyle.customMobileWidthPct > 0) &&
      `fp-fld-d-${btnStyle.align || "center"}`,
    (btnStyle.customWidthPct > 0 || btnStyle.customMobileWidthPct > 0) &&
      `fp-fld-m-${btnStyle.mobileAlign || btnStyle.align || "center"}`
  );
  const buttonResizeVars: React.CSSProperties = {
    ...(btnStyle.customWidthPct > 0
      ? { ["--fp-fld-w-d" as string]: `${btnStyle.customWidthPct}%` }
      : {}),
    ...(btnStyle.customMobileWidthPct > 0
      ? { ["--fp-fld-w-m" as string]: `${btnStyle.customMobileWidthPct}%` }
      : {}),
    ...(btnStyle.customHeightPx > 0
      ? { ["--fp-fld-h-d" as string]: `${btnStyle.customHeightPx}px` }
      : {}),
    ...(btnStyle.customMobileHeightPx > 0
      ? { ["--fp-fld-h-m" as string]: `${btnStyle.customMobileHeightPx}px` }
      : {}),
    ...(btnStyle.fontSizePx > 0
      ? { ["--fp-btn-fs-d" as string]: `${btnStyle.fontSizePx}px` }
      : {}),
    ...(btnStyle.mobileFontSizePx > 0
      ? { ["--fp-btn-fs-m" as string]: `${btnStyle.mobileFontSizePx}px` }
      : {}),
  };

  const nameField = welcomeContent?.name_field.enabled && (
    <div data-fp-edit="inputs" data-fp-field="name" className={fieldResizeCls} style={fieldResizeStyle}>
      <Input
        type="text"
        placeholder={welcomeContent.name_field.placeholder}
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value)}
        disabled={fieldsLocked}
        className={cn(
          "fp-input border-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors",
          fieldHeightCls,
          isInteractionDisabled && "cursor-not-allowed opacity-60"
        )}
        style={inputStyles(!!localErrors.name)}
        required={welcomeContent.name_field.required}
      />
      {localErrors.name && (
        <p className={fieldErrorCls} style={fieldErrorStyle}>{localErrors.name}</p>
      )}
    </div>
  );

  const emailField = welcomeContent?.email_field.enabled && (
    <div data-fp-edit="inputs" data-fp-field="email" className={fieldResizeCls} style={fieldResizeStyle}>
      <Input
        type="email"
        placeholder={welcomeContent.email_field.placeholder}
        value={formData.email}
        onChange={(e) => handleInputChange("email", e.target.value)}
        disabled={fieldsLocked}
        className={cn(
          "fp-input border-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors",
          fieldHeightCls,
          isInteractionDisabled && "cursor-not-allowed opacity-60"
        )}
        style={inputStyles(!!localErrors.email)}
        required={welcomeContent.email_field.required}
      />
      {localErrors.email && (
        <p className={fieldErrorCls} style={fieldErrorStyle}>{localErrors.email}</p>
      )}
    </div>
  );

  const whatsappField = welcomeContent?.whatsapp_field.enabled && (
    <div data-fp-edit="inputs" data-fp-field="whatsapp" className={fieldResizeCls} style={fieldResizeStyle}>
      <div
        className={cn(
          "fp-input-wrap flex border-2 transition-colors overflow-hidden",
          isInteractionDisabled && "opacity-60"
        )}
        style={{
          backgroundColor: step.elements.inputs.background,
          borderColor: localErrors.whatsapp_number
            ? errorBorderColor
            : step.elements.inputs.borderColor,
          borderRadius: step.elements.inputs.radius,
          borderWidth: `${step.elements.inputs.borderWidth ?? 2}px`,
        }}
      >
        <div
          className={cn(
            "flex items-center border-r-2",
            tallPills ? "pl-6 pr-3" : pillInputs ? "pl-4 pr-3" : "px-3"
          )}
          style={{ borderColor: step.elements.inputs.borderColor }}
        >
          <WhatsAppIcon
            className="w-5 h-5"
            style={{ color: step.elements.icons.accentColor }}
          />
        </div>
        <Input
          type="tel"
          placeholder={welcomeContent.whatsapp_field.placeholder}
          value={formData.whatsapp_number}
          onChange={(e) => handleInputChange("whatsapp_number", e.target.value)}
          disabled={fieldsLocked}
          className={cn(
            "border-0 rounded-none bg-transparent focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
            fieldHeightCls,
            isInteractionDisabled && "cursor-not-allowed"
          )}
          style={{ color: step.elements.inputs.textColor }}
          required={welcomeContent.whatsapp_field.enabled}
          maxLength={21}
        />
      </div>
      {localErrors.whatsapp_number && (
        <p className={fieldErrorCls} style={fieldErrorStyle}>
          {localErrors.whatsapp_number}
        </p>
      )}
    </div>
  );

  // Birthday row: an optional line + three numeric boxes (year / month / day),
  // each styled like the other inputs. Nothing renders unless enabled.
  const birthdayCfg = welcomeContent?.birthday_field;
  const birthdayField = isGate && birthdayCfg?.enabled && (
    <div data-fp-edit="inputs" data-fp-field="birthday" className={fieldResizeCls} style={fieldResizeStyle}>
      {birthdayCfg.label && (
        <p
          className={cn("fp-rich", subheadingAlignCls)}
          style={{
            ...subHeadingStyles,
            ...(birthdayCfg.color ? { color: birthdayCfg.color } : {}),
            marginBottom: 8,
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(birthdayCfg.label) }}
        />
      )}
      <div className="grid grid-cols-3" style={{ gap: 10 }}>
        {(
          [
            ["y", birthdayCfg.placeholders?.year || "YYYY", "bday-year", "Birth year"],
            ["m", birthdayCfg.placeholders?.month || "MM", "bday-month", "Birth month"],
            ["d", birthdayCfg.placeholders?.day || "DD", "bday-day", "Birth day"],
          ] as const
        ).map(([part, placeholder, autoComplete, label]) => (
          <Input
            key={part}
            type="text"
            inputMode="numeric"
            autoComplete={autoComplete}
            aria-label={label}
            placeholder={placeholder}
            value={birthParts[part]}
            onChange={(e) => handleBirthdayPart(part, e.target.value)}
            disabled={fieldsLocked}
            maxLength={part === "y" ? 4 : 2}
            className={cn(
              "fp-input border-2 text-center focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors",
              fieldHeightCls,
              isInteractionDisabled && "cursor-not-allowed opacity-60"
            )}
            style={inputStyles(!!localErrors.birthday)}
          />
        ))}
      </div>
      {localErrors.birthday && (
        <p className={fieldErrorCls} style={fieldErrorStyle}>{localErrors.birthday}</p>
      )}
    </div>
  );

  // Single-select choice groups (e.g. "I'm shopping for: Women / Men / Baby").
  // Real radios stay in the DOM (sr-only) for keyboard + screen-reader
  // support; the visible circle mirrors the checked state.
  const choiceFields =
    isGate &&
    (welcomeContent?.choice_fields || [])
      .filter((cf) => cf.enabled && cf.key && (cf.options?.length || 0) > 0)
      .map((cf) => {
        const cfColor = cf.color || step.elements.inputs.textColor;
        const err = localErrors.choices?.[cf.key];
        return (
          <div
            key={cf.key}
            data-fp-edit="inputs"
            data-fp-field="choices"
            className={fieldResizeCls}
            style={fieldResizeStyle}
          >
            <div className="fp-choice text-left" style={{ color: cfColor }}>
              <span className="block" style={{ fontWeight: 700, marginBottom: "10px" }}>
                {cf.label}
              </span>
              <div className="flex flex-wrap items-center" style={{ gap: "12px 28px" }}>
                {cf.options.map((opt) => {
                  const selected = formData.extra_fields?.[cf.key] === opt;
                  return (
                    <label
                      key={opt}
                      className={cn(
                        "flex items-center gap-2",
                        isInteractionDisabled ? "cursor-not-allowed" : "cursor-pointer"
                      )}
                    >
                      <input
                        type="radio"
                        name={`fp-choice-${cf.key}`}
                        value={opt}
                        checked={selected}
                        onChange={() => handleChoiceChange(cf.key, opt)}
                        disabled={fieldsLocked}
                        className="sr-only"
                      />
                      <span
                        aria-hidden="true"
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          border: `1px solid ${err ? errorBorderColor : cfColor}`,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          backgroundColor: step.elements.inputs.background,
                        }}
                      >
                        {selected && (
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: cfColor,
                            }}
                          />
                        )}
                      </span>
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
              {err && (
                <p className={fieldErrorCls} style={fieldErrorStyle}>
                  {err}
                </p>
              )}
            </div>
          </div>
        );
      });

  // Field order: `welcome.field_order` when set, else the historical
  // name → email → whatsapp → birthday → choices. Any field omitted from a partial
  // list is appended in default order, so a short list can never make a
  // configured field vanish; unknown/duplicate keys are dropped.
  const DEFAULT_FIELD_ORDER: PopupFieldKey[] = [
    "name",
    "email",
    "whatsapp",
    "birthday",
    "choices",
  ];
  const fieldNodes: Record<PopupFieldKey, React.ReactNode> = {
    name: nameField,
    email: emailField,
    whatsapp: whatsappField,
    birthday: birthdayField,
    choices: choiceFields,
  };
  const configuredOrder = (welcomeContent?.field_order || []).filter(
    (k): k is PopupFieldKey => DEFAULT_FIELD_ORDER.includes(k as PopupFieldKey)
  );
  const resolvedOrder = [
    ...new Set([...configuredOrder, ...DEFAULT_FIELD_ORDER]),
  ];
  const orderedFields = resolvedOrder.map((key) => (
    <React.Fragment key={key}>{fieldNodes[key]}</React.Fragment>
  ));

  const formFields = isGate && (
    <div
      className={cn(
        // hasVisual, NOT layoutImage: a loyalty-pass popup is a two-column
        // layout exactly like an image one, but layoutImage only counts
        // IMAGES — so pass popups were getting the roomy single-column
        // spacing (24px) beside their visual instead of the tight 12px.
        !hasVisual && (isSlidein ? "space-y-3" : "space-y-5 md:space-y-6"),
        hasVisual && "space-y-3",
        inputsSpace.className,
        fieldGap.className
      )}
      style={{ ...inputsSpace.style, ...fieldGap.style }}
    >
      {orderedFields}
    </div>
  );

  // On a success step that carries a pass, the popup's OWN button is the pass
  // button — same styling, same slot, the one the merchant configured. It is
  // greyed out until the pass lands, then getting the card and dismissing are
  // a single tap. No second button is introduced anywhere.
  const passMode = !isWelcome && !isIntro && !isQuestion && !isPhone && !isWheel && !!passPreview?.wallet_button;
  // The builder preview never polls, so it must show the RESTING state — without
  // this exemption a designer would stare at a permanently greyed button.
  const passPending =
    passMode && !isInteractionDisabled && !issuedPass && issuedPassStatus !== "done";
  const passReady = passMode && (!!issuedPass || isInteractionDisabled);

  const handlePassClick = () => {
    if (isInteractionDisabled || !issuedPass) return;
    window.open(issuedPass.install_url, "_blank", "noopener,noreferrer");
    onClose();
  };

  // Wheel with the form BEFORE it: the lone Spin button (no form to submit).
  const handleWheelOnlySpin = () => {
    if (isInteractionDisabled) {
      startDemoSpin();
      return;
    }
    if (!wheelBusy) onWheelSpin?.();
  };
  // Pick-a-card: the tapped card IS the spin. Beside a form it validates and
  // submits like the button would; on a game-only page it just plays.
  const handleCardPick = () => {
    if (isInteractionDisabled) {
      startDemoSpin();
      return;
    }
    if (wheelBusy) return;
    if (wheelInline) {
      if (!validateForm()) return;
      onWheelSpin?.({
        ...formData,
        whatsapp_number: !welcomeContent?.whatsapp_field?.enabled
          ? ""
          : formData.whatsapp_number
            ? formatWhatsAppNumber(formData.whatsapp_number)
            : formData.whatsapp_number,
      });
      return;
    }
    onWheelSpin?.();
  };

  const submitButtonEl = (
    <Button
      type={isGate ? "submit" : "button"}
      onClick={
        isGate
          ? undefined
          : isWheel
            ? handleWheelOnlySpin
            : isInteractionDisabled
              ? undefined
              : isIntro
                ? onIntroAdvance
                : passReady
                  ? handlePassClick
                  : passPending
                    ? undefined
                    : handleButtonClick
      }
      // Previews stay inert EXCEPT the wheel's spin, which runs the demo.
      disabled={
        isWheel
          ? isSubmitting || wheelBusy
          : isSubmitting || isInteractionDisabled || passPending
      }
      data-fp-edit="button"
      className={cn(
        "fp-btn font-semibold transition-all duration-200",
        // The content column is justify-center. A pass popup renders ONE button
        // where there used to be two, so without this the survivor re-centres
        // upward and lands mid-panel instead of where the Close button always
        // sat. mt-auto pins it back to the bottom of the panel.
        passMode && !isBar && "mt-auto",
        buttonResizeCls,
        isBar
          ? "h-10 w-full px-6 text-sm whitespace-nowrap"
          : cn(
              "w-full",
              step.elements.button.size === "sm" && "h-10 text-sm md:text-base",
              step.elements.button.size === "md" && "h-12 md:h-14 text-base md:text-lg",
              step.elements.button.size === "lg" && "h-14 md:h-16 text-lg md:text-xl"
            ),
        litButton && "fp-btn-lit",
        !litButton && (!isInteractionDisabled || isWheel) && "hover:scale-105 active:scale-95",
        isInteractionDisabled && !isWheel && "cursor-not-allowed opacity-60",
        buttonSpace.className
      )}
      style={{ ...buttonStyles, ...buttonResizeVars, ...buttonSpace.style }}
    >
      {isWheel ? (
        wheelBusy || isSubmitting ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{wheelContent?.spinning_text || WHEEL_DEFAULTS.spinning_text}</span>
          </div>
        ) : (
          wheelContent?.button_text ||
            (wheelContent?.game === "plinko" ? wheelContent.plinko_button_text || "Tap to drop" : WHEEL_DEFAULTS.button_text)
        )
      ) : isWelcome ? (
        isSubmitting ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Processing...</span>
          </div>
        ) : (
          welcomeContent?.button_text
        )
      ) : passPending ? (
        <span className="flex items-center justify-center gap-2">
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-current/30"
            style={{ borderTopColor: "currentColor" }}
          />
          <span>Preparing your pass…</span>
        </span>
      ) : passReady ? (
        <span className="flex items-center justify-center gap-2">
          <img src={PASSES_MARK} alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
          <span>Get my pass</span>
        </span>
      ) : isIntro ? (
        introContent?.button_text || "Continue"
      ) : (
        successContent?.button_text
      )}
    </Button>
  );
  // Pick-a-card: the cards are the call to action, so no button below them.
  const submitButton = isCardsGame ? null : submitButtonEl;

  // ── Submission error banner ───────────────────────────────────────────────
  // Every step that can FIRE a submission must be able to SHOW it failing.
  // Until 22 Sep 2026 this only rendered on the gate, so a deck that finished
  // and was then refused by the server (a duplicate signup, an offline phone)
  // sat on its completion card for ever with nothing on screen and no way
  // forward. The steps with no submit button of their own also get a retry,
  // because re-pressing something is otherwise impossible there.
  const errorBanner = globalErrorMessage ? (
    <div
      className="rounded-lg border px-4 py-3 text-sm"
      style={
        panelIsLight
          ? { backgroundColor: "#fef2f2", borderColor: "#fecaca", color: "#b91c1c" }
          : {
              backgroundColor: "rgba(255,255,255,0.1)",
              borderColor: "rgba(255,255,255,0.25)",
              color: "#fecaca",
            }
      }
      role="alert"
      aria-live="assertive"
    >
      {globalErrorMessage}
    </div>
  ) : null;

  // The stages that replace the whole panel body (deck, scratch, standalone
  // wheel) render the banner themselves — an inline wheel is a gate and shows
  // it inside the content column, so it must not be drawn twice.
  const stageErrorBanner =
    errorBanner && !wheelInline ? (
      <div className="mx-auto mt-3 flex w-full max-w-md flex-col items-stretch gap-2 px-4">
        {errorBanner}
        {onRetrySubmit && (
          <button
            type="button"
            onClick={onRetrySubmit}
            className="self-center text-sm underline underline-offset-2"
            style={{ color: errorTextColor }}
          >
            Try again
          </button>
        )}
      </div>
    ) : null;

  // ── Success-step discount code (dashed box + copy control) ────────────────
  // Code source order: the one minted for THIS signup, else the static code
  // configured on the step. Neither = render nothing (an empty dashed box
  // reads as a broken promise of a discount).
  const codeCfg = successContent?.discount_code;
  const resolvedCode = (issuedDiscountCode || codeCfg?.code || "").trim();
  // The minted code arrives a few seconds AFTER the success step first paints.
  // Reserving the box from the first frame (in a loading state) keeps the panel
  // the same size throughout — the layout jump and image re-crop that a
  // late-appearing box caused are gone, and the wait reads as intentional.
  // Popups that never poll (static code, previews) keep the old rule exactly.
  const codeLoading = !resolvedCode && issuedCodeStatus === "pending";
  const codeMissed = !resolvedCode && issuedCodeStatus === "done";
  const showCodeBlock =
    !isWelcome &&
    !isIntro &&
    !isQuestion && !isPhone &&
    !isWheel &&
    codeCfg?.enabled === true &&
    (!!resolvedCode || codeLoading || codeMissed);
  const [codeCopied, setCodeCopied] = useState(false);
  useEffect(() => {
    if (!codeCopied) return;
    const t = setTimeout(() => setCodeCopied(false), 2000);
    return () => clearTimeout(t);
  }, [codeCopied]);

  const copyCode = useCallback(async () => {
    if (!resolvedCode || isInteractionDisabled) return;
    try {
      await navigator.clipboard.writeText(resolvedCode);
      setCodeCopied(true);
    } catch {
      // Clipboard API needs a secure context and can be blocked outright.
      // Fall back to selecting the text so the visitor can copy by hand —
      // never leave the tap doing visibly nothing.
      const el = document.getElementById(`fp-code-${popupId || "x"}`);
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [resolvedCode, isInteractionDisabled, popupId]);

  // The code line keeps the copy control's width clear on the right; a
  // centred code also clears it on the left so the code is centred in the
  // BOX, not in what is left beside the control.
  const codeLinePad: React.CSSProperties = {
    textAlign: codeAlign,
    paddingRight: 28,
    paddingLeft: codeAlign === "center" ? 28 : 0,
  };
  const discountCodeEl = showCodeBlock ? (
    <div className="w-full">
      {codeCfg?.label && (
        <p
          data-fp-edit="code"
          className={cn("fp-rich leading-relaxed mb-3", codeAlignCls, codeLabelMobile.className)}
          style={{ ...codeLabelStyles, ...codeLabelMobile.style }}
          dangerouslySetInnerHTML={{
            __html: sanitizeInlineHtml(codeCfg.label),
          }}
        />
      )}
      <button
        type="button"
        onClick={copyCode}
        aria-label={
          resolvedCode
            ? `${codeCfg?.copy_text || "Copy code"}: ${resolvedCode}`
            : "Getting your code"
        }
        aria-busy={codeLoading || undefined}
        className={cn(
          "group relative w-full flex items-center px-4 py-3.5 rounded-md",
          "border-2 border-dashed transition-colors",
          resolvedCode && !isInteractionDisabled && "hover:bg-black/[0.03] active:bg-black/[0.06]",
          (!resolvedCode || isInteractionDisabled) && "cursor-default"
        )}
        style={{
          // Inherit the step's text colour so the box works on light AND
          // dark panels; the dashed edge is that colour at low opacity.
          color: messageStyles?.color,
          borderColor: panelIsLight
            ? "rgba(0,0,0,0.35)"
            : "rgba(255,255,255,0.45)",
        }}
      >
        {resolvedCode ? (
          <span
            id={`fp-code-${popupId || "x"}`}
            className={cn("w-full min-w-0 font-semibold tracking-wide text-[17px] md:text-[18px] truncate", codeAlignCls)}
            style={codeLinePad}
          >
            {resolvedCode}
          </span>
        ) : (
          // Same font metrics as the code line, so the box height never moves
          // between the loading, fallback and filled states.
          <span
            className={cn("w-full min-w-0 font-semibold tracking-wide text-[17px] md:text-[18px] truncate opacity-70", codeAlignCls)}
            style={codeLinePad}
          >
            {codeLoading ? (
              <span className="inline-flex items-center gap-2.5 animate-pulse">
                <span
                  aria-hidden="true"
                  className="w-4 h-4 shrink-0 rounded-full border-2 border-current border-t-transparent animate-spin"
                />
                Getting your code…
              </span>
            ) : (
              "Check your WhatsApp for your code"
            )}
          </span>
        )}
        <span
          className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 text-[13px] transition-opacity",
            // The copy control sits at the box's right edge in every state; it
            // only becomes visible (and meaningful) once a code exists.
            resolvedCode ? "opacity-70 group-hover:opacity-100" : "opacity-0"
          )}
          aria-hidden={!resolvedCode || undefined}
        >
          {codeCopied ? (
            <>
              <Check className="w-4 h-4" />
              {codeCfg?.copied_text || "Copied!"}
            </>
          ) : (
            <CopyGlyph />
          )}
        </span>
      </button>
    </div>
  ) : null;

  // ── Spin-to-win: prize block on the success step + urgency countdown ─────
  const isSuccessStep = !isWelcome && !isIntro && !isQuestion && !isPhone && !isDeck && !isScratch && !isWheel;
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const countdownEnd = wheelExpiresAt ? Date.parse(wheelExpiresAt) : NaN;
  const countdownActive =
    isSuccessStep && !!wheelResult && Number.isFinite(countdownEnd) && countdownEnd > countdownNow;
  useEffect(() => {
    if (!countdownActive) return;
    // Re-read the clock the moment the timer becomes active: the initial
    // value dates from mount, so the first frame otherwise shows a few
    // seconds MORE than the configured window.
    setCountdownNow(Date.now());
    const t = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [countdownActive]);
  // Resolved here (after resolvedCode) and handed to the CTA via the ref.
  wheelApplyRef.current =
    isSuccessStep && !isInteractionDisabled && typeof window !== "undefined"
      ? autoApplyUrl(wheelContent, resolvedCode || null, window.location.href)
      : null;
  // result_badge:false hides the prize pill (the copy then names the prize
  // through {prize}); the countdown stays.
  const showPrizeBadge = wheelContent?.result_badge !== false;
  const wheelPrizeEl =
    isSuccessStep && wheelResult && (showPrizeBadge || Number.isFinite(countdownEnd)) ? (
      <div className="w-full text-center" data-fp-edit="body">
        {showPrizeBadge && (
        <div
          role="status"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            maxWidth: "100%",
            padding: "9px 18px",
            borderRadius: 999,
            fontWeight: 700,
            fontSize: "clamp(15px, 2.2vh, 19px)",
            lineHeight: 1.25,
            background: wheelResult.win
              ? step.elements.button.background
              : panelIsLight
                ? "#f3f4f6"
                : "rgba(255,255,255,0.1)",
            color: wheelResult.win
              ? step.elements.button.textColor
              : messageStyles?.color,
          }}
        >
          <span aria-hidden="true">{wheelResult.win ? "🎉" : "🙈"}</span>
          <span>{resultHeading(wheelContent, wheelResult)}</span>
        </div>
        )}
        {Number.isFinite(countdownEnd) && wheelResult.win && (
          <p
            className="mt-2"
            style={{
              ...messageStyles,
              fontSize: "13px",
              opacity: 0.85,
              fontVariantNumeric: "tabular-nums",
            }}
            aria-live="polite"
          >
            {(wheelContent?.countdown_label || WHEEL_DEFAULTS.countdown_label).replace(
              /\{time\}/gi,
              formatCountdown(countdownEnd - countdownNow)
            )}
          </p>
        )}
      </div>
    ) : null;

  // {prize} in the thank-you copy of a wheel popup (lib/wheel fillPrizeToken).
  const fillPrize = (text: string | undefined, role: "heading" | "line") =>
    isSuccessStep && wheelContent?.enabled ? fillPrizeToken(text, wheelContent, wheelResult, role) : text;
  const stepHeading = isIntro
    ? introContent?.heading
    : isQuestion
      ? questionContent?.heading
    : isPhone
      ? phoneContent?.heading
    : isWelcome
      ? welcomeContent?.heading
      : isDeck
        ? deckContent?.heading
        : isScratch
          ? scratchContent?.heading
          : isWheel
            ? wheelContent?.heading
            : fillPrize(successContent?.heading, "heading");
  const stepSubheading = isIntro
    ? introContent?.sub_heading
    : isQuestion
      ? questionContent?.sub_heading
    : isPhone
      ? phoneContent?.sub_heading
    : isWelcome
      ? welcomeContent?.sub_heading
      : isWheel
        ? wheelContent?.sub_heading
        : fillPrize(successContent?.sub_heading, "line");
  const stepFooter = isIntro
    ? introContent?.footer_text
    : isWelcome
      ? welcomeContent?.footer_text
      : isWheel
        ? wheelContent?.footer_text
        : fillPrize(successContent?.footer_text, "line");
  const successMessage = fillPrize(successContent?.message, "line");

  // Per-step text image: designer artwork that REPLACES the heading +
  // description block (copy baked into the image). The live heading text
  // stays in the row and doubles as the alt text / aria-label.
  const textImage = step.textImage?.url ? step.textImage : null;
  const textImageAlt =
    (stepHeading || "").replace(/<[^>]*>/g, "").trim() || "Popup heading";
  // Builder drag-resize on the artwork (fp-ti-*): 0 = auto full width.
  const tiW = textImage && textImage.widthPct > 0 ? textImage.widthPct : 0;
  const tiWM =
    textImage && textImage.mobileWidthPct > 0 ? textImage.mobileWidthPct : 0;
  const textImageEl =
    textImage &&
    withMobileSource(
      <img
        src={textImage.url}
        alt={textImageAlt}
        data-fp-edit="textimage"
        className={cn(
          "block w-full h-auto",
          tiW > 0 && "fp-ti-d",
          tiWM > 0 && "fp-ti-m",
          // alignment is per-device: mobileAlign falls back to align
          (tiW > 0 || tiWM > 0) && `fp-ti-d-${textImage.align || "center"}`,
          (tiW > 0 || tiWM > 0) &&
            `fp-ti-m-${textImage.mobileAlign || textImage.align || "center"}`
        )}
        style={{
          ...(tiW > 0 ? { ["--fp-ti-d" as string]: `${tiW}%` } : {}),
          ...(tiWM > 0 ? { ["--fp-ti-m" as string]: `${tiWM}%` } : {}),
        }}
      />,
      textImage.mobileUrl
    );

  
/** Trademark glyphs (R-in-circle / TM) at heading sizes: shrink to a true
 *  superscript via .fp-tm — many fonts draw them at full cap height.
 *  Applied AFTER sanitization; injects only our own class-bearing span. */
const withSmallTrademarks = (html: string): string =>
  html.replace(/([\u00AE\u2122])/g, '<span class="fp-tm">$1</span>');

// Same layout classes the legacy renderer put on the <form> / success <div>.
  // Slide-ins get a tighter vertical rhythm — the corner card shouldn't
  // inherit the big modal's spacing.
  const innerLayoutClasses = cn(
    "mx-auto w-full",
    !hasVisual &&
      (isSlidein ? "space-y-4 max-w-md" : "space-y-6 md:space-y-8 max-w-md"),
    hasVisual && "space-y-3 md:space-y-4 max-w-sm"
  );
  // Content-column cap: design-controlled since 10 Aug 2026. The legacy
  // hardcoded 360px cap made the Padding preset a no-op on any popup wider
  // than ~400px — the centered column, not the padding, owned the side
  // white space.
  const contentCap =
    typeof step.size.contentMaxWidth === "number" && step.size.contentMaxWidth > 0
      ? `min(100%, ${Math.round(step.size.contentMaxWidth)}px)`
      : FORM_MAX_WIDTH;
  const innerLayoutStyle: React.CSSProperties = {
    maxWidth: contentCap,
    width: "100%",
  };
  // Builder edge-drag on the content column — the thing that actually owns the
  // white space beside the text (the panel can be wide while the column stays
  // narrow, and vice versa). Only bind the live-drag var when a cap is set;
  // absent class = the untouched FORM_MAX_WIDTH default.
  const contentDragProps = {
    "data-fp-edit": "content",
    className: cn(
      innerLayoutClasses,
      typeof step.size.contentMaxWidth === "number" &&
        step.size.contentMaxWidth > 0 &&
        "fp-cw"
    ),
    style: {
      ...innerLayoutStyle,
      // The var carries contentCap VERBATIM (not a bare px), so the !important
      // rule resolves to exactly what the inline maxWidth already computed —
      // the class is a live-drag hook, never a change in rendered width. The
      // overlay overwrites the var with plain px only while dragging.
      ...(typeof step.size.contentMaxWidth === "number" &&
      step.size.contentMaxWidth > 0
        ? { ["--fp-cw" as string]: contentCap }
        : {}),
    } as React.CSSProperties,
  };

  // Brand mark above the heading. Question and wheel pages had their own; the
  // form and success steps carry one too (a logo on every page — the Alia
  // layout puts it in the frame, not in a step).
  const stepLogo = isQuestion
    ? questionContent?.logo_url
      ? { url: questionContent.logo_url, width: questionContent.logo_width, mobileWidth: questionContent.logo_mobile_width }
      : null
    : isWheel
      ? wheelContent?.logo_url
        ? { url: wheelContent.logo_url, width: wheelContent.logo_width, mobileWidth: wheelContent.logo_mobile_width }
        : null
      : isPhone
        ? phoneContent?.logo_url
          ? { url: phoneContent.logo_url, width: phoneContent.logo_width, mobileWidth: phoneContent.logo_mobile_width }
          : null
      : isIntro
        ? introContent?.logo_url
          ? { url: introContent.logo_url, width: introContent.logo_width, mobileWidth: introContent.logo_mobile_width }
          : null
      : isWelcome
        ? welcomeContent?.logo_url
          ? { url: welcomeContent.logo_url, width: welcomeContent.logo_width, mobileWidth: welcomeContent.logo_mobile_width }
          : null
        : isSuccessStep && successContent?.logo_url
          ? { url: successContent.logo_url, width: successContent.logo_width, mobileWidth: successContent.logo_mobile_width }
          : null;

  const contentInner = (
    <>
      {/* Header */}
      <div
        className={cn(
          "text-center",
          // Card images bring their own inset, so the stacked mobile layout
          // doesn't need the full flush-image breathing room above the text.
          isSlidein
            ? "pt-0"
            : isCardImage && visualPosition !== "bottom"
              ? "pt-1"
              : "pt-4",
          !hasVisual &&
            (isSlidein ? "space-y-1.5 mb-3" : "space-y-3 md:space-y-4 mb-4 md:mb-6"),
          hasVisual && "space-y-1 md:space-y-2 md:pt-0"
        )}
      >
        {textImageEl ? (
          textImageEl
        ) : (
          <>
            {stepLogo?.url && (
              <img
                src={stepLogo.url}
                alt=""
                draggable={false}
                // Builder drag-resize writes content_settings.<step>.logo_width,
                // which only exists for the form and thank-you logos.
                data-fp-edit={isWelcome || isSuccessStep ? "logo" : undefined}
                className={stepLogo.mobileWidth ? "fp-w-m" : undefined}
                style={{
                  width: Math.max(24, stepLogo.width ?? 96),
                  maxWidth: "60%",
                  height: "auto",
                  margin: "0 auto 12px",
                  display: "block",
                  ...(stepLogo.mobileWidth
                    ? ({ "--fp-w-m": `${Math.max(24, stepLogo.mobileWidth)}px` } as React.CSSProperties)
                    : {}),
                }}
              />
            )}
            {isQuestion && questionContent?.eyebrow && (
              <p
                className={cn("fp-rich", subheadingAlignCls, subheadingMobile.className)}
                style={{ ...subHeadingStyles, ...subheadingMobile.style, marginBottom: 6 }}
                dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(questionContent.eyebrow) }}
              />
            )}
            {stepHeading && (
              <h2
                className={cn("fp-rich leading-tight", headingAlignCls, headingMobile.className, headingSpace.className)}
                data-fp-edit="heading"
                style={{ ...headingStyles, ...headingMobile.style, ...headingSpace.style }}
                dangerouslySetInnerHTML={{ __html: withSmallTrademarks(sanitizeInlineHtml(stepHeading)) }}
              />
            )}
            {stepSubheading && (
              <p
                className={cn("fp-rich leading-relaxed", subheadingAlignCls, subheadingMobile.className, subheadingSpace.className)}
                data-fp-edit="subheading"
                style={{ ...subHeadingStyles, ...subheadingMobile.style, ...subheadingSpace.style }}
                dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(stepSubheading) }}
              />
            )}
          </>
        )}
      </div>

      {/* Spin-to-win: what they won, right under the heading. */}
      {wheelPrizeEl}

      {/* Success: the copy-code box sits directly under the heading block,
          above any message panel — the position it holds in Klaviyo/BSS
          popups, and where the visitor looks first for their code. */}
      {discountCodeEl}

      {/* Welcome: fields — Intro: copy only — Success: message */}
      {isGate ? (
        formFields
      ) : isPhone && phoneContent ? (
        <PhonePage
          content={phoneContent}
          step={step}
          inert={isInteractionDisabled}
          onSubmit={(n) => onPhoneSubmit?.(n)}
          onSkip={() => onPhoneSkip?.()}
        />
      ) : isQuestion && questionContent ? (
        <QuestionOptions
          question={questionContent}
          step={step}
          inert={isInteractionDisabled}
          onAnswer={(k, v) => onQuestionAnswer?.(k, v)}
          onDismiss={() => {
            if (!isInteractionDisabled) onClose();
          }}
        />
      ) : isIntro || isWheel ? null : (
        successMessage && (
          <div
            data-fp-edit="body"
            className={cn(
              "fp-rich leading-relaxed text-center",
              bodyAlignCls,
              bodySpace.className,
              // Without an image the message is a visible card. Beside an
              // image it used to keep a 16 px inset with no box, so it never
              // lined up with the heading and could not look left-aligned
              // (Matt, 7 Oct 2026); it now sits flush like the other copy.
              !hasVisual && "p-6 md:p-8 rounded-lg border"
            )}
            style={{
              ...messageStyles,
              ...bodySpace.style,
              // Light panels keep the exact legacy gray-50 card; dark/colored
              // panels get a translucent surface instead of a foreign box.
              ...(!hasVisual
                ? panelIsLight
                  ? { backgroundColor: "#f9fafb", borderColor: "#f3f4f6" }
                  : {
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderColor: "rgba(255,255,255,0.14)",
                    }
                : {}),
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeInlineHtml(successMessage),
            }}
          />
        )
      )}

      {/* The gate has its own submit button; the question and phone pages fire
          submissions too and used to swallow the failure in silence. */}
      {(isGate || isQuestion || isPhone) && errorBanner}

      {!isQuestion && !isPhone && (isGate || isIntro || successContent?.button_text || passMode) && submitButton}

      {/* "No thanks" dismiss link — closes the popup like the ✕, but as
          visible configurable text under the CTA. Renders on the INTRO page
          when one is active (the SVYP pattern: decline the gate, not the
          form), else on the welcome form; inert in the editor's static
          preview. Styled fully INLINE (no utility classes) so the shell's
          !important block can't touch it. */}
      {(isIntro || (isGate && !introContent?.enabled)) &&
        design.dismiss?.enabled &&
        design.dismiss.text && (
        <button
          type="button"
          data-fp-edit="dismiss"
          onClick={() => {
            if (!isInteractionDisabled) onClose();
          }}
          style={
            design.dismiss.style === "button"
              ? {
                  // full-width secondary button, CTA-shaped
                  display: "block",
                  width: "100%",
                  background: design.dismiss.background || "#f3f4f6",
                  color: design.dismiss.color || "#374151",
                  border: "none",
                  borderRadius:
                    design.dismiss.radius || step.elements.button.radius,
                  padding: "12px 24px",
                  fontSize:
                    design.dismiss.fontSizePx > 0
                      ? `${design.dismiss.fontSizePx}px`
                      : "14px",
                  fontWeight: 600,
                  textAlign: "center",
                  cursor: isInteractionDisabled ? "default" : "pointer",
                  fontFamily: "inherit",
                }
              : {
                  // subtle text link
                  display: "block",
                  marginLeft: "auto",
                  marginRight: "auto",
                  background: "transparent",
                  border: "none",
                  cursor: isInteractionDisabled ? "default" : "pointer",
                  color: design.dismiss.color || "#6b7280",
                  fontSize:
                    design.dismiss.fontSizePx > 0
                      ? `${design.dismiss.fontSizePx}px`
                      : "13px",
                  textDecoration:
                    design.dismiss.underline === false ? "none" : "underline",
                  textUnderlineOffset: "3px",
                  padding: "2px 8px",
                  fontFamily: "inherit",
                }
          }
        >
          {design.dismiss.text}
        </button>
      )}

      {stepFooter && (
        <p
          className={cn("fp-rich text-center leading-relaxed", footerAlignCls, footerSpace.className)}
          data-fp-edit="footer"
          style={{ ...footerStyles, ...footerSpace.style }}
          dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(stepFooter) }}
        />
      )}

      {/* "Powered by Flowapt" (per store, feature_flags.popup_powered_by).
          Last line of the content column so it never sits on the artwork.
          Fully INLINE styles: the store's CSS (and the shell's !important
          block) cannot restyle or hide it. Footer colour, reduced strength. */}
      {poweredByUrl && (
        <a
          href={poweredByUrl}
          target="_blank"
          rel="noopener"
          data-fp-powered-by
          style={{
            display: "block",
            marginTop: "14px",
            textAlign: "center",
            fontSize: "11px",
            lineHeight: 1.4,
            letterSpacing: "0.01em",
            color: (footerStyles.color as string) || "inherit",
            opacity: 0.6,
            textDecoration: "none",
            fontFamily: "inherit",
          }}
        >
          Powered by <span style={{ fontWeight: 600 }}>Flowapt</span>
        </a>
      )}
    </>
  );

  const contentSection = (
    <div
      className={cn(
        "flex flex-col",
        !hasVisual && "w-full h-auto",
        hasVisual && visualPosition && getMobileContentClasses(visualPosition),
        hasVisual &&
          (visualPosition === "top" || visualPosition === "bottom") &&
          "md:w-full md:flex-1 md:p-6",
        hasVisual &&
          (visualPosition === "left" || visualPosition === "right") &&
          "md:flex-1 md:py-6 md:px-8 md:h-auto md:w-auto md:justify-center",
        // Card images: explicit paddings via fp-card-content (CSS vars +
        // !important in RENDERER_CSS) — a touch more room under the footer
        // on the stacked mobile layout, and a tighter image→text gap on
        // desktop (the card's own inset already separates them). A rule, not
        // utilities, so the compiled utility order can't flip it.
        isCardImage && "fp-card-content"
      )}
      style={
        !hasVisual
          ? { padding: `${pads.soloY} ${pads.soloX}` }
          : isCardImage
            ? ({
                ["--fp-cc-pad-mobile" as string]: "12px 12px 20px",
                ["--fp-cc-pad-md" as string]:
                  visualPosition === "left"
                    ? "24px 32px 24px 16px"
                    : visualPosition === "right"
                      ? "24px 16px 24px 32px"
                      : "24px",
              } as React.CSSProperties)
            : undefined
      }
    >
      {isGate ? (
        <form onSubmit={handleSubmit} {...contentDragProps}>
          {contentInner}
        </form>
      ) : (
        <div {...contentDragProps}>{contentInner}</div>
      )}
    </div>
  );

  // Bar: three zones on desktop — image far left, heading + subheading in the
  // middle, and the regular popup's STACKED form column (fields on top of
  // each other + button) on the right. Mobile drops the image and stacks.
  const barInner = (
    <div className="flex w-full items-stretch">
      {/* full-bleed image column: full height, natural width from the image's
          aspect ratio, capped at a third of the bar */}
      {layoutImage?.url && (
        <div className="hidden md:block md:max-w-[33.333%] md:flex-none md:self-stretch">
          <img
            src={layoutImage.url}
            alt={layoutImage.alt || "Popup image"}
            className={cn(
              "h-full w-auto max-w-full",
              layoutImage.fit === "contain" ? "object-contain" : "object-cover"
            )}
          />
        </div>
      )}
      <div className="min-w-0 flex-1 px-4 py-3 pr-12 md:px-8 md:pr-14">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-3 md:flex-row md:items-center md:justify-center md:gap-8">
        <div className="min-w-0 w-full md:w-auto md:flex-1">
          {textImageEl ? (
            textImageEl
          ) : (
            <>
              {stepHeading && (
                <h2
                  className={cn("fp-rich leading-tight", headingAlignCls, headingMobile.className, headingSpace.className)}
                  data-fp-edit="heading"
                  style={{ ...headingStyles, ...headingMobile.style, ...headingSpace.style }}
                  dangerouslySetInnerHTML={{ __html: withSmallTrademarks(sanitizeInlineHtml(stepHeading)) }}
                />
              )}
              {stepSubheading && (
                <p
                  className={cn("fp-rich mt-1 leading-snug", subheadingAlignCls, subheadingMobile.className, subheadingSpace.className)}
                  data-fp-edit="subheading"
                  style={{ ...subHeadingStyles, ...subheadingMobile.style, ...subheadingSpace.style }}
                  dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(stepSubheading) }}
                />
              )}
            </>
          )}
        </div>
        {isWelcome ? (
          <form
            onSubmit={handleSubmit}
            className="flex w-full flex-col items-stretch gap-2 md:max-w-md md:flex-1"
          >
            {nameField}
            {emailField}
            {whatsappField}
            {choiceFields}
            {submitButton}
          </form>
        ) : (
          <div className="flex w-full flex-col items-stretch gap-2 md:max-w-md md:flex-1">
            {!isIntro && successMessage && (
              <p
                data-fp-edit="body"
                className={cn("fp-rich leading-snug", bodyAlignCls, bodySpace.className)}
                style={{ ...messageStyles, ...bodySpace.style }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeInlineHtml(successMessage),
                }}
              />
            )}
            {(isIntro || successContent?.button_text) && submitButton}
          </div>
        )}
      </div>
      {globalErrorMessage && isWelcome && (
        <p
          className="mt-2 text-center text-xs"
          style={{ color: errorTextColor }}
          role="alert"
          aria-live="assertive"
        >
          {globalErrorMessage}
        </p>
      )}
      {stepFooter && (
        <p
          className={cn("fp-rich mt-1.5 text-center", footerAlignCls, footerSpace.className)}
          data-fp-edit="footer"
          style={{ ...footerStyles, fontSize: "11px", ...footerSpace.style }}
          dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(stepFooter) }}
        />
      )}
      </div>
    </div>
  );

  // Swipe deck: replaces the whole panel body (no image/content split). The
  // panel chrome — overlay, format, entry animation, close button — is reused
  // untouched. Static previews render the stack but never advance.
  const deckInner =
    isDeck && deckContent ? (
      <DeckStage
        deck={deckContent}
        step={step}
        panelIsLight={panelIsLight}
        inert={isInteractionDisabled}
        onSwipe={onDeckSwipe}
        onComplete={(answers) => {
          if (!isInteractionDisabled) onDeckComplete?.(answers);
        }}
      />
    ) : null;

  // Scratch card: replaces the whole panel body like the deck. The CTA only
  // navigates (nothing persists), so the inert preview just shows the cover.
  const scratchInner =
    isScratch && scratchContent ? (
      <ScratchStage
        scratch={scratchContent}
        step={step}
        panelIsLight={panelIsLight}
        inert={isInteractionDisabled}
        onReveal={onScratchReveal}
        onAdvance={() => {
          if (!isInteractionDisabled) onScratchAdvance?.();
        }}
      />
    ) : null;

  // Fullscreen centers content with auto margins (scrollable on overflow,
  // where justify-content:center would clip the top). The game steps replace
  // the whole panel body, so they need the same wrapper or they top-align.
  // Content panel (Alia's Opal desktop layout): on a full-screen page the
  // whole content block sits in a styled box aligned left / centre / right
  // over the backdrop. Phones can opt out (IM8 keeps its copy plain there).
  const cp = design.contentPanel;
  const panelOn =
    format === "fullscreen" && !!cp?.enabled && !(isPhoneViewport && cp.phones === "plain");
  const panelAlignStyle: React.CSSProperties = panelOn
    ? {
        display: "flex",
        justifyContent: cp!.align === "right" ? "flex-end" : cp!.align === "left" ? "flex-start" : "center",
        paddingLeft: cp!.inset || "0px",
        paddingRight: cp!.inset || "0px",
        boxSizing: "border-box",
      }
    : {};
  const fullscreenPanel = (inner: React.ReactNode) =>
    panelOn ? (
      <div
        data-fp-content-panel
        style={{
          background: cp!.background,
          borderRadius: cp!.radius,
          padding: cp!.padding,
          width: "100%",
          maxWidth: cp!.maxWidth || "100%",
          boxSizing: "border-box",
        }}
      >
        {inner}
      </div>
    ) : (
      inner
    );
  const centeredInFullscreen = (inner: React.ReactNode) =>
    format === "fullscreen" ? (
      <div
        className="w-full h-auto"
        style={{ marginTop: "auto", marginBottom: "auto", ...panelAlignStyle }}
      >
        {fullscreenPanel(inner)}
      </div>
    ) : (
      inner
    );

  // Spin-to-win: the wheel takes the visual slot, the content column keeps
  // its heading/fields/button/footer — the same two-column rhythm as an
  // image popup, so every design control (padding, fonts, colours) still
  // applies. Stacks on phones (wheel on top). "before" placement renders
  // the wheel with a lone Spin button, centred.
  const wheelStageEl =
    isWheel && wheelContent ? (
      wheelContent.game === "cards" ? (
        <CardsStage
          wheel={wheelContent}
          segments={wheelSegments}
          step={step}
          panelIsLight={panelIsLight}
          status={effectiveWheelStatus}
          resultIndex={effectiveWheelIndex}
          onPick={handleCardPick}
          onLanded={wheelDemo ? onDemoLanded : onWheelLanded}
          onSettled={wheelDemo ? undefined : onWheelSettled}
        />
      ) : wheelContent.game === "plinko" ? (
        <PlinkoStage
          wheel={wheelContent}
          segments={wheelSegments}
          step={step}
          panelIsLight={panelIsLight}
          status={effectiveWheelStatus}
          resultIndex={effectiveWheelIndex}
          onLanded={wheelDemo ? onDemoLanded : onWheelLanded}
          onSettled={wheelDemo ? undefined : onWheelSettled}
          inert={false}
        />
      ) : (
        <WheelStage
          wheel={wheelContent}
          segments={wheelSegments}
          step={step}
          panelIsLight={panelIsLight}
          status={effectiveWheelStatus}
          resultIndex={effectiveWheelIndex}
          onLanded={wheelDemo ? onDemoLanded : onWheelLanded}
          onSettled={wheelDemo ? undefined : onWheelSettled}
          inert={false}
        />
      )
    ) : null;
  const wheelSideCls = wheelContent?.wheel_side === "right" ? "fp-oh-right" : "fp-oh-left";
  const wheelSizePx =
    wheelContent?.size && wheelContent.size > 0 ? wheelContent.size : WHEEL_DEFAULTS.size;
  const overhangShift =
    typeof wheelContent?.overhang_shift === "number" ? wheelContent.overhang_shift : 0;
  const overhangGap =
    typeof wheelContent?.overhang_gap === "number" && wheelContent.overhang_gap >= 0
      ? wheelContent.overhang_gap
      : 32;
  // Content column padding = the part of the wheel that sits INSIDE the
  // panel (half the wheel, plus any inward shift) + the gap.
  const overhangPad = Math.max(0, wheelSizePx / 2 + overhangShift) + overhangGap;
  const wheelInner =
    isWheel && wheelContent ? (
      <div
        className={cn(
          "flex w-full items-center",
          wheelInline
            ? cn(
                "flex-col md:flex-row md:items-center",
                wheelContent.wheel_side === "right" && "md:flex-row-reverse",
                wheelOverhang && "fp-wheel-oh"
              )
            : // Pick-a-card reads top-down like a page: logo + heading, THEN the
              // cards (the wheel keeps its disc-on-top stack).
              isCardsGame
              ? "flex-col-reverse"
              : "flex-col"
        )}
        style={{
          padding: `${pads.soloY} ${pads.soloX}`,
          gap: "clamp(18px, 3vw, 40px)",
          ...(wheelOverhang
            ? {
                ["--fp-wheel-size" as string]: `${wheelSizePx}px`,
                ["--fp-wheel-oh-shift" as string]: `${overhangShift}px`,
                ["--fp-wheel-oh-pad" as string]: `${Math.round(overhangPad)}px`,
              }
            : {}),
        }}
      >
        <div
          className={cn(
            "flex w-full shrink-0 items-center justify-center",
            wheelInline && !wheelOverhang && "md:w-1/2",
            wheelOverhang && cn("fp-wheel-oh-stage", wheelSideCls)
          )}
          // The pointer + glow overhang the disc; keep them clear of the panel edge.
          style={{ padding: "8% 6%" }}
        >
          {wheelStageEl}
        </div>
        <div
          className={cn(
            "w-full",
            wheelInline && !wheelOverhang && "md:w-1/2",
            wheelOverhang && cn("fp-wheel-oh-content", wheelSideCls)
          )}
        >
          {wheelInline ? (
            <form onSubmit={handleSubmit} {...contentDragProps}>
              {contentInner}
            </form>
          ) : (
            <div {...contentDragProps}>{contentInner}</div>
          )}
        </div>
      </div>
    ) : null;

  const panelInnerBody = isWheel ? (
    centeredInFullscreen(
      <>
        {wheelInner}
        {stageErrorBanner}
      </>
    )
  ) : isScratch ? (
    centeredInFullscreen(
      <>
        {scratchInner}
        {stageErrorBanner}
      </>
    )
  ) : isDeck ? (
    centeredInFullscreen(
      <>
        {deckInner}
        {stageErrorBanner}
      </>
    )
  ) : isBar ? (
    barInner
  ) : (
    <div
      // Full-height card with a side image: the row fills the card so the
      // photo column runs top to bottom (Alia's Aether desktop). Otherwise
      // auto margins center fullscreen content vertically but stay scrollable
      // when it overflows (justify-content:center would clip the top). A
      // phone sheet with its photo stacked on top pins to the top instead, so
      // the photo meets the screen edge and spare height falls below the form
      // rather than as a band of panel colour above the photo.
      className={cn("w-full", cardFillRow ? "h-full" : "h-auto")}
      style={
        format === "fullscreen" && !cardFillRow
          ? phonePhotoOnTop
            ? { marginTop: 0, marginBottom: "auto" }
            : { marginTop: "auto", marginBottom: "auto" }
          : undefined
      }
    >
      <div
        className={cn(
          cardFillRow ? "flex h-full" : "flex h-auto",
          !hasVisual && "flex-col",
          hasVisual && "md:flex",
          hasVisual && visualPosition === "top" && "md:flex-col",
          hasVisual && visualPosition === "bottom" && "md:flex-col-reverse",
          hasVisual && visualPosition === "right" && "md:flex-row-reverse md:items-stretch",
          hasVisual && visualPosition === "left" && "md:flex-row md:items-stretch",
          hasVisual &&
            visualPosition &&
            getMobileFlexDirection(visualPosition)
        )}
      >
        {visualSection}
        {contentSection}
      </div>
    </div>
  );
  // Form / phone / success pages: the same panel box, wrapped around the
  // page body (the game pages wrap inside centeredInFullscreen).
  const panelInner =
    panelOn && !isWheel && !isScratch && !isDeck && !isBar ? (
      <div className="w-full" style={{ marginTop: "auto", marginBottom: "auto", ...panelAlignStyle }}>
        {fullscreenPanel(panelInnerBody)}
      </div>
    ) : (
      panelInnerBody
    );

  // ---------- format wrappers ----------

  const panel = (
    <div
      ref={panelRef}
      // Success gets a FRESH mount so the theme's entry animation replays —
      // without this the panel swaps content in place and the finish reads
      // as a hard cut (sharpest after the deck's completion card). The flow
      // steps (intro/welcome/deck) share one mount: no per-page re-animation.
      key={currentStep === "success" ? "step-success" : "step-flow"}
      tabIndex={-1}
      role="dialog"
      aria-modal={isModalLike ? true : undefined}
      aria-label={stepHeading || "Popup"}
      // Builder edge-drag: only the centered modal has a width worth dragging
      // (fullscreen fills, bar is full-width, slide-in is corner-fixed).
      data-fp-edit={format === "modal" ? "panel" : undefined}
      className={cn(
        "flowapt-animated relative outline-none",
        mobileBackdrop && "fp-bg-m",
        // The overhanging wheel must cross the panel's bounds on desktop;
        // phones stack it inside, so they keep the clip.
        frameless ? "overflow-visible" : wheelOverhang ? "overflow-hidden md:overflow-visible" : "overflow-hidden",
        format === "modal" &&
          frameless &&
          // Floating panel: sized like the modal but never scroll-clipped —
          // the deck's fly-out animation must cross the invisible bounds.
          "w-full mx-4 md:mx-0 max-w-[92vw] sm:max-w-[86vw] md:max-w-[1300px] lg:max-w-[1500px] h-auto",
        format === "modal" &&
          !frameless &&
          // max-h-full (not a vh cap): vh is the LARGEST viewport on phones,
          // so a 95vh panel pokes below the screen while the URL bar shows —
          // its internal scroll then "finishes" with the last ~15% (button/
          // footer) unreachable. The fixed inset-0 wrapper always matches the
          // VISIBLE viewport, so sizing against it keeps the whole panel on
          // screen. overscroll-contain stops the page behind stealing the
          // scroll at the boundary.
          "w-full mx-4 md:mx-0 max-w-[92vw] sm:max-w-[86vw] md:max-w-[1300px] lg:max-w-[1500px] h-auto max-h-full overflow-y-auto overscroll-contain md:max-h-none md:overflow-y-visible",
        format === "fullscreen" && "overflow-y-auto",
        isSlidein && "max-h-[85vh] overflow-y-auto",
        isBar && "max-h-[45vh] overflow-y-auto"
      )}
      style={fitScale < 1 ? { ...panelStyles, scale: String(fitScale) } : panelStyles}
    >
      {closeButton}
      {panelInner}
    </div>
  );

  return (
    <>
      <style>{RENDERER_CSS}</style>

      {isModalLike ? (
        // Blocking formats: overlay + centered panel. Fullscreen must DROP
        // the p-4 class, not just inline padding:0 — host pages (incl. our
        // own landing shell) ship rem→px blocks like `.p-4{padding:16px
        // !important}` that beat inline styles, leaving a 16px ring around
        // what should be a full-bleed takeover.
        <div
          className={cn(
            "fixed inset-0 z-50 flex items-center justify-center",
            format !== "fullscreen" && "p-4",
            // Phone card: size the frame to the DYNAMIC viewport (100dvh), not
            // inset-0 — inside a transformed/100vh host the fixed box reaches
            // under Safari's toolbar and the bottom margin never shows.
            cardMode && "fp-phone-card"
          )}
          style={format === "fullscreen" ? { padding: cardMode ? cardInset : 0 } : overlayCss(step.theme)}
          onMouseDown={(e) => {
            // opt-in click-outside close — legacy popups keep overlayClose=false
            if (
              format === "modal" &&
              design.overlayClose &&
              !isInteractionDisabled &&
              !isPageEmbed &&
              e.target === e.currentTarget
            ) {
              onClose();
            }
          }}
        >
          {format === "fullscreen" && (
            <div className="absolute inset-0" style={overlayCss(step.theme)} />
          )}
          {panel}
          {screenCloseButton}
        </div>
      ) : isBar ? (
        // Banner bar: pinned full-width, no overlay. The wrapper lets clicks
        // pass through to the page; only the bar itself captures events.
        <div
          className="fixed inset-x-0 z-50"
          style={{
            ...(design.position === "bottom" ? { bottom: 0 } : { top: 0 }),
            pointerEvents: "none",
          }}
        >
          <div style={{ pointerEvents: "auto" }}>{panel}</div>
        </div>
      ) : (
        // Slide-in: pinned corner card, no overlay, page stays interactive
        <div
          className="fixed z-50"
          style={{
            ...(design.position === "bottom_left" || design.position === "top_left"
              ? { left: "20px" }
              : { right: "20px" }),
            ...(design.position === "top_left" || design.position === "top_right"
              ? { top: "20px" }
              : { bottom: "20px" }),
            pointerEvents: "auto",
          }}
        >
          {panel}
        </div>
      )}

      {/* No drag-editable elements on the deck step — the overlay only knows
          welcome/success anchors, so it stays unmounted there. The INTRO wears
          the welcome design, so its overlay edits map onto the welcome step. */}
      {editMode && currentStep !== "deck" && currentStep !== "scratch" && currentStep !== "wheel" && currentStep !== "question" && currentStep !== "phone" && (
        <EditOverlay
          panelRef={panelRef}
          currentStep={currentStep === "intro" ? "welcome" : currentStep}
          aligns={{
            textimage: {
              d: step.textImage?.align ?? "center",
              m: step.textImage?.mobileAlign || step.textImage?.align || "center",
            },
            inputs: {
              d: step.elements.inputs.align ?? "center",
              m:
                step.elements.inputs.mobileAlign ||
                step.elements.inputs.align ||
                "center",
            },
            button: {
              d: step.elements.button.align ?? "center",
              m:
                step.elements.button.mobileAlign ||
                step.elements.button.align ||
                "center",
            },
            heading: {
              d: step.elements.heading.align ?? "center",
              m:
                step.elements.heading.mobileAlign ||
                step.elements.heading.align ||
                "center",
            },
            subheading: {
              d: step.elements.subheading.align ?? "center",
              m:
                step.elements.subheading.mobileAlign ||
                step.elements.subheading.align ||
                "center",
            },
            code: { d: codeAlign, m: codeMobileAlign || codeAlign },
            body: {
              d: step.elements.body.align ?? "center",
              m:
                step.elements.body.mobileAlign ||
                step.elements.body.align ||
                "center",
            },
            footer: {
              d: step.elements.footer.align ?? "center",
              m:
                step.elements.footer.mobileAlign ||
                step.elements.footer.align ||
                "center",
            },
          }}
        />
      )}
    </>
  );
};

export default PopupRenderer;
